import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import {
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  FormControl,
  FormGroup,
  Box,
  Button,
  IconButton,
  Avatar,
  Typography,
  Autocomplete,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  RadioGroup,
  Radio,
  InputAdornment,
} from "@mui/material";
import axios from "axios";
import useFormHandler from "../useFormHandler";
import CommonFormLayout from "./CommonFormLayout";
import { MAIN_URL } from "../../../Configurations/Urls";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  applyLeadContactTimingIstFields,
  inferPreferredTimezoneFromLead,
} from "../../../Utils/DateAndTime";
import { emsRowMatchesLoggedInTrainer } from "../../../Utils/emsTrainerUserMatch";
import { filterLeadsForStudentEnrollment } from "../../../Utils/leadEnrollment";
import {
  getInstallmentDueDateOrderError,
  getMinDueDateForInstallmentRow,
  getMinDueDateForStandaloneInstallment,
  getStandaloneFeeInstallmentDueDateError,
} from "../../../Utils/feeInstallmentDueDates";
import {
  buildFeeInstallmentRows,
  canCreateMoreFeeInstallments,
  getExpectedInstallmentAmount,
  sumInstallmentAmounts,
  validateFeeInstallmentAmounts,
} from "../../../Utils/feeInstallmentAmounts";
import {
  FEE_INSTALLMENT_ALREADY_PAID_MSG,
  getPaidFeeReceiptInstallmentIds,
  validateFeeInstallmentStatusChange,
} from "../../../Utils/feeInstallmentReceipts";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import ReviewRatingIcon, {
  REVIEW_RATING_BY_VALUE,
  formatReviewRatingLabel,
} from "../../../Utils/ReviewRatingIcon";
import {
  formatDateTime,
  validateActivityDatetimeAgainstLead,
} from "../FormConfigurations";

const buildLeadDatetimeHint = (leadDatetimeRaw) => {
  const formatted = formatDateTime(leadDatetimeRaw);
  return formatted ? `Lead date & time: ${formatted}` : "";
};

const buildActivityDatetimeHelperText = (errorMsg, leadDatetimeRaw) => {
  const hint = buildLeadDatetimeHint(leadDatetimeRaw);
  if (errorMsg && hint) {
    return (
      <>
        {errorMsg}
        <br />
        {hint}
      </>
    );
  }
  return errorMsg || hint || "";
};

const replaceOrgIdInApiLink = (link, organizationId) => {
  if (!link) return link;
  const orgId = organizationId != null ? String(organizationId) : "";
  let path = link
    .replace(/\$\{organization_id\}/g, orgId)
    .replace(/\$\{org_id\}/g, orgId)
    .replace(/\{organization_id\}/g, orgId)
    .replace(/\{org_id\}/g, orgId);
  if (!path.startsWith("/api/") && /^\/organizations\//.test(path)) {
    path = `/api${path}`;
  }
  return path;
};

const formatDdMmYyyy = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

/** Training program id on a lead row (API shape may vary). */
const getLeadTrainingProgramId = (lead) => {
  if (!lead || typeof lead !== "object") return null;
  return (
    lead.organization_ems_training_program_id ??
    lead.training_program_id ??
    lead.training_program?.organization_ems_training_program_id ??
    lead.training_program?.training_program_id ??
    null
  );
};

const toDatetimeLocalInputValueEmbed = (d = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const apiDatetimeToDatetimeLocalEmbed = (v) => {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  if (s.includes("T")) return s.slice(0, 16);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  if (m) return `${m[1]}T${m[2]}`;
  return s;
};

/** `<input type="time">` expects `HH:mm`. */
const toTimeInputValue = (v) => {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  return s.length >= 5 ? s.slice(0, 5) : s;
};

function CommonForm({
  validateForm,
  baseUrl,
  initialData,
  successMessages,
  redirectPath,
  headerProps,
  Inputs,
  DataFetchers,
  fieldGridLayout,
  embeddedParticipantInputs = [],
  embeddedParticipantValidateForm,
  embeddedLeadActivityConfig = null,
}) {
  const normalizeApiArray = (res) => {
    const d = res?.data;
    if (Array.isArray(d?.userTypes)) return d.userTypes;
    if (Array.isArray(d?.user_types)) return d.user_types;
    if (Array.isArray(d?.persons)) return d.persons;
    if (Array.isArray(d?.organization_users)) return d.organization_users;
    if (Array.isArray(d?.users)) return d.users;
    if (Array.isArray(d?.countries)) return d.countries;
    if (Array.isArray(d?.states)) return d.states;
    if (Array.isArray(d?.cities)) return d.cities;
    if (Array.isArray(d?.userTypes?.data)) return d.userTypes.data;
    if (Array.isArray(d?.user_types?.data)) return d.user_types.data;
    if (Array.isArray(d?.data?.users)) return d.data.users;
    if (Array.isArray(d?.data?.organization_users)) return d.data.organization_users;
    if (Array.isArray(d?.data?.data)) return d.data.data; // paginated shape
    if (Array.isArray(d?.data)) return d.data;
    return [];
  };

  /**
   * Unifies old OrganizationPerson payload and newer OrganizationUser/ApplicationUser payloads
   * for Trainers/Employees dropdowns while keeping both person/user ids available.
   */
  const normalizeTrainerEmployeeOption = (item) => {
    const orgPerson =
      item?.organization_person ??
      item?.person ??
      item?.employee ??
      item?.trainer ??
      null;
    const appUser =
      item?.application_user ??
      item?.user ??
      (Array.isArray(item?.applicationuser) ? item.applicationuser[0] : null) ??
      (Array.isArray(item?.application_user) ? item.application_user[0] : null) ??
      null;
    const fullName =
      String(item?.full_name || "").trim() ||
      String(item?.person_full_name || "").trim() ||
      String(orgPerson?.full_name || "").trim() ||
      String(item?.organization_person?.person?.full_name || "").trim() ||
      String(appUser?.full_name || "").trim() ||
      "";
    const firstName =
      item?.first_name ??
      orgPerson?.first_name ??
      appUser?.first_name ??
      "";
    const lastName =
      item?.last_name ??
      orgPerson?.last_name ??
      appUser?.last_name ??
      "";
    const userTypeRaw =
      item?.user_type_name ??
      item?.user_type ??
      item?.application_user?.user_type_name ??
      item?.application_user_type ??
      item?.application_user?.user_type ??
      item?.application_user?.application_user_type ??
      item?.organization_user?.user_type_name ??
      item?.organization_user?.user_type ??
      item?.organization_user?.application_user_type ??
      appUser?.user_type ??
      appUser?.application_user_type ??
      item?.role_name ??
      item?.application_user?.role_name ??
      item?.organization_user?.role_name ??
      "";
    const userTypeLabel = String(userTypeRaw || "").trim();
    return {
      ...item,
      organization_person_id:
        item?.organization_person_id ??
        item?.trainer_employee_id ??
        item?.organization_person?.organization_person_id ??
        item?.person?.organization_person_id ??
        item?.organization_user?.organization_person_id ??
        item?.employee?.organization_person_id ??
        item?.trainer?.organization_person_id ??
        "",
      organization_user_id:
        item?.organization_user_id ??
        item?.organization_user?.organization_user_id ??
        appUser?.organization_user_id ??
        "",
      organization_user_type_id:
        item?.organization_user_type_id ??
        item?.organization_user?.organization_user_type_id ??
        null,
      application_user_id:
        item?.application_user_id ??
        appUser?.application_user_id ??
        "",
      person_type_id:
        item?.person_type_id ??
        item?.organization_person?.person_type_id ??
        item?.person?.person_type_id ??
        item?.employee?.person_type_id ??
        item?.trainer?.person_type_id ??
        null,
      person_code:
        item?.person_code ??
        item?.organization_person?.person_code ??
        item?.person?.person_code ??
        "",
      full_name: fullName || [firstName, lastName].filter(Boolean).join(" ").trim(),
      first_name: firstName,
      last_name: lastName,
      email: item?.email ?? appUser?.email ?? orgPerson?.email ?? "",
      username:
        item?.username ??
        appUser?.username ??
        appUser?.user_name ??
        item?.application_user_name ??
        "",
      user_type_name:
        item?.user_type_name ??
        item?.organization_user?.user_type_name ??
        item?.application_user?.user_type_name ??
        "",
      user_type_label: userTypeLabel,
    };
  };

  const navigate = useNavigate();
  const location = useLocation();
  const { id: demoSessionEditId } = useParams();
  const { id: feeInstallmentEditId } = useParams();

  const isSameValue = (a, b) => {
    if (a === b) return true;
    if (
      a === null ||
      a === undefined ||
      a === "" ||
      b === null ||
      b === undefined ||
      b === ""
    ) {
      return false;
    }
    return String(a) === String(b);
  };

  const getBatchRunStatus = (batch) =>
    String(batch?.status ?? "").trim().toLowerCase();

  const isOngoingBatchRecord = (batch) => getBatchRunStatus(batch) === "ongoing";

  const getBatchClassRunStatus = (batchClass, allBatches = []) => {
    const nestedStatus = batchClass?.batch?.status;
    if (nestedStatus != null && String(nestedStatus).trim() !== "") {
      return String(nestedStatus).trim().toLowerCase();
    }
    const batchId =
      batchClass?.organization_ems_batch_id ??
      batchClass?.batch?.organization_ems_batch_id;
    if (!batchId) return "";
    const matched = allBatches.find((b) =>
      isSameValue(b?.organization_ems_batch_id, batchId)
    );
    return getBatchRunStatus(matched);
  };

  const filterOngoingBatchesForForm = (batches, currentFormData, currentMode) =>
    (Array.isArray(batches) ? batches : []).filter((batch) => {
      if (
        (currentMode === "edit" || currentMode === "view") &&
        isSameValue(
          batch?.organization_ems_batch_id,
          currentFormData?.organization_ems_batch_id
        )
      ) {
        return true;
      }
      return isOngoingBatchRecord(batch);
    });

  const getOptionSelectValue = (option, menuKey, optionMainKey) => {
    if (!option) return "";
    const primary = menuKey == null ? option : option?.[menuKey];
    if (primary !== null && primary !== undefined && String(primary).trim() !== "") {
      return primary;
    }
    if (optionMainKey === "Employees" || optionMainKey === "Trainers") {
      return (
        option?.organization_user_id ??
        option?.organization_user?.organization_user_id ??
        option?.application_user_id ??
        ""
      );
    }
    return primary ?? "";
  };

  const embedParticipantFormsRef = useRef([]);
  const demoParticipantsHydratedRef = useRef(false);
  /** Tracks admission selection for Batch Students → enrollment_date default. */
  const prevBatchAdmissionIdForEnrollmentRef = useRef(undefined);
  /** Tracks admission clears on Batch Students → clear linked student. */
  const prevBatchStudentAdmissionIdRef = useRef(undefined);
  const leadActivityEmbedRef = useRef(null);
  const leadActivityEmbedHydratedRef = useRef(false);
  const [leadActivityEmbed, setLeadActivityEmbed] = useState(null);
  const [leadActivityEmbedErrors, setLeadActivityEmbedErrors] = useState({});
  const [leadActivityEmbedTouched, setLeadActivityEmbedTouched] = useState(false);
  const [personTypeId, setPersonTypeId] = useState("");
  const leadContactTimingEditInitRef = useRef(false);
  const leadContactTimingAutofillLeadIdRef = useRef(null);
  const prevAdmissionTrainingProgramIdRef = useRef(null);
  const admissionMissingFeePopupRef = useRef(null);
  const feeInstallmentInitialStatusRef = useRef(null);

  const {
    mode,
    formData,
    setFormData,
    formErrors,
    setFormErrors,
    handleSubmit,
    loading,
    btnLoading,
    userData,
    handleChange,
    handleChangeImage,
    handleFileChange,
    handleRemoveFile,
    handlePreview,
  } = useFormHandler({
    baseUrl,
    initialData,
    validateForm,
    successMessages,
    redirectPath,
  });

  useEffect(() => {
    leadActivityEmbedRef.current = leadActivityEmbed;
  }, [leadActivityEmbed]);

  useEffect(() => {
    if (!embeddedLeadActivityConfig || baseUrl !== "/api/ems/leads") return;
    const raw = leadActivityEmbedRef.current || {};
    const hasIntent = ["activity_datetime", "lead_direction", "activity_type"].some(
      (k) => raw[k] != null && String(raw[k]).trim() !== ""
    );
    const hasExistingActivity =
      raw?.organization_ems_lead_activity_id != null &&
      String(raw.organization_ems_lead_activity_id).trim() !== "";
    const shouldAttach =
      mode === "add" ||
      hasExistingActivity ||
      (hasIntent && leadActivityEmbedTouched);
    const payload = shouldAttach
      ? {
          ...raw,
          // Always send stage along with lead activity payload.
          organization_ems_lead_stage_id:
            raw?.organization_ems_lead_stage_id ??
            formData?.lead_stage_id ??
            "",
          // "Activity By" dropdown selection (Employee/Interns).
          person_type_id:
            raw?.person_type_id ??
            (personTypeId !== "" ? personTypeId : undefined),
        }
      : null;
    setFormData((prev) => {
      if (JSON.stringify(prev?.lead_activity ?? null) === JSON.stringify(payload)) {
        return prev;
      }
      return { ...prev, lead_activity: payload };
    });
  }, [
    embeddedLeadActivityConfig,
    baseUrl,
    leadActivityEmbed,
    leadActivityEmbedTouched,
    mode,
    formData?.lead_stage_id,
    personTypeId,
    setFormData,
  ]);

  const [selectOptions, setSelectOptions] = useState({});
  const { Permission } = usePermissionDataStore();
  const token = localStorage.getItem("token");
  const FEE_RECEIPT_ALL_PAID_MSG =
    "All installments are paid and receipts are made for this student.";
  // Prefer `formData.organization_id` (from useFormHandler) because the login
  // payload shape may vary across environments.
  const organizationId =
    formData?.organization_id ??
    userData?.organization?.organization_id ??
    userData?.organization_id ??
    userData?.user_active_organization?.organization_id ??
    null;

  const applyLeadActivityLeadConstraint = useCallback(
    async (leadId, leadRow, activityDatetime) => {
      if (!leadId) {
        setFormData((prev) => ({ ...prev, _lead_datetime: "" }));
        setFormErrors((prev) => ({ ...prev, activity_datetime: "" }));
        return;
      }

      let leadDt = apiDatetimeToDatetimeLocalEmbed(leadRow?.lead_datetime);
      if (!leadDt && organizationId) {
        try {
          const res = await axios.get(
            `${MAIN_URL}/api/ems/leads/${encodeURIComponent(String(leadId))}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: { organization_id: organizationId },
            }
          );
          const row = res?.data?.data ?? res?.data;
          leadDt = apiDatetimeToDatetimeLocalEmbed(row?.lead_datetime);
        } catch {
          leadDt = "";
        }
      }

      setFormData((prev) => ({ ...prev, _lead_datetime: leadDt || "" }));

      const activityDt =
        activityDatetime !== undefined && activityDatetime !== null
          ? activityDatetime
          : formData?.activity_datetime;
      const msg = validateActivityDatetimeAgainstLead(activityDt, leadDt);
      setFormErrors((prev) => ({
        ...prev,
        activity_datetime: msg || "",
      }));
    },
    [
      formData?.activity_datetime,
      organizationId,
      setFormData,
      setFormErrors,
      token,
    ]
  );

  const hasPermission = useCallback(
    (action) => {
      if (Permission == null || !Array.isArray(Permission) || !action) return false;
      const tokenized = String(action)
        .split("||")
        .map((x) => String(x).trim().toUpperCase())
        .filter(Boolean);
      if (!tokenized.length) return false;
      return tokenized.some((tokenKey) =>
        Permission.some((p) => String(p).trim().toUpperCase() === tokenKey)
      );
    },
    [Permission]
  );
  const canViewSelfBatchClassRecords = hasPermission("VIEW_SELF_BATCH_CLASS_RECORDS");
  const canViewAllBatchClassRecords = hasPermission("VIEW_BATCH_CLASS_RECORD");
  const canViewSelfClassAttendance = hasPermission("VIEW_SELF_CLASS_ATTENDANCE");
  const canViewAllClassAttendance = hasPermission("VIEW_CLASS_ATTENDANCES");
  const isViewMode = mode === "view";
  const isDemoSessionForm = baseUrl === "/api/ems/demo-sessions";
  const isDemoSessionParticipantEmbed =
    isDemoSessionForm && (mode === "add" || mode === "edit");
  const isDemoSessionEditEmbed =
    isDemoSessionForm && mode === "edit" && demoSessionEditId;
  const isDemoParticipantsAddMode =
    baseUrl === "/api/ems/demo-participants" && mode === "add";
  const isTrainingProgramForm = baseUrl === "/api/ems/training-programs";
  const isAdmissionsForm = baseUrl === "/api/ems/admissions";
  const isLeadContactTimingForm = baseUrl === "/api/ems/lead-contact-timings";

  useEffect(() => {
    if (
      baseUrl !== "/api/ems/batch-trainers" &&
      baseUrl !== "/api/ems/batch-classes" &&
      baseUrl !== "/api/ems/demo-sessions"
    ) {
      return;
    }
    const currentTrainerValue = formData?.trainer_employee_id;
    if (
      currentTrainerValue == null ||
      String(currentTrainerValue).trim() === ""
    ) {
      return;
    }
    const trainerOptions = Array.isArray(selectOptions?.Trainers)
      ? selectOptions.Trainers
      : [];
    if (!trainerOptions.length) return;

    const hasUserIdMatch = trainerOptions.some((opt) =>
      isSameValue(
        opt?.organization_user_id ?? opt?.organization_user?.organization_user_id,
        currentTrainerValue
      )
    );
    if (hasUserIdMatch) return;

    const personMatched = trainerOptions.find((opt) =>
      isSameValue(
        opt?.organization_person_id ??
          opt?.organization_person?.organization_person_id ??
          opt?.organization_user?.organization_person_id,
        currentTrainerValue
      )
    );
    const mappedOrganizationUserId =
      personMatched?.organization_user_id ??
      personMatched?.organization_user?.organization_user_id ??
      "";
    if (
      mappedOrganizationUserId !== "" &&
      !isSameValue(mappedOrganizationUserId, currentTrainerValue)
    ) {
      setFormData((prev) => ({
        ...prev,
        trainer_employee_id: mappedOrganizationUserId,
      }));
    }
  }, [
    baseUrl,
    formData?.trainer_employee_id,
    selectOptions?.Trainers,
    setFormData,
  ]);

  useEffect(() => {
    if (!isLeadContactTimingForm || isViewMode) return;
    const leadId = formData?.organization_ems_lead_id;
    if (!leadId) {
      leadContactTimingAutofillLeadIdRef.current = null;
      return;
    }
    const idStr = String(leadId);

    if (mode === "edit" && !leadContactTimingEditInitRef.current) {
      leadContactTimingEditInitRef.current = true;
      leadContactTimingAutofillLeadIdRef.current = idStr;
      return;
    }

    if (leadContactTimingAutofillLeadIdRef.current === idStr) return;

    const applyFromLeadRow = (leadRow) => {
      const tz = inferPreferredTimezoneFromLead(leadRow);
      if (!tz) return false;
      const refYmd =
        leadRow?.lead_datetime && String(leadRow.lead_datetime).length >= 10
          ? String(leadRow.lead_datetime).slice(0, 10)
          : new Date().toISOString().slice(0, 10);
      setFormData((prev) => {
        const draft = { ...prev, preferred_contact_timezone: tz };
        applyLeadContactTimingIstFields(draft, refYmd);
        return {
          ...prev,
          preferred_contact_timezone: draft.preferred_contact_timezone,
          morning_student_time_start: toTimeInputValue(
            draft.morning_student_time_start
          ),
          morning_student_time_end: toTimeInputValue(
            draft.morning_student_time_end
          ),
          evening_student_time_start: toTimeInputValue(
            draft.evening_student_time_start
          ),
          evening_student_time_end: toTimeInputValue(
            draft.evening_student_time_end
          ),
          morning_ist_time_start: toTimeInputValue(draft.morning_ist_time_start),
          morning_ist_time_end: toTimeInputValue(draft.morning_ist_time_end),
          evening_ist_time_start: toTimeInputValue(draft.evening_ist_time_start),
          evening_ist_time_end: toTimeInputValue(draft.evening_ist_time_end),
        };
      });
      leadContactTimingAutofillLeadIdRef.current = idStr;
      return true;
    };

    const leads = selectOptions?.Leads || [];
    const fromList = leads.find((l) =>
      isSameValue(l?.organization_ems_lead_id, leadId)
    );
    if (fromList && applyFromLeadRow(fromList)) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${MAIN_URL}/api/ems/leads/${encodeURIComponent(idStr)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params:
              organizationId !== null && organizationId !== undefined
                ? { organization_id: organizationId }
                : undefined,
          }
        );
        const data = res.data?.data ?? res.data;
        if (cancelled || !data) {
          leadContactTimingAutofillLeadIdRef.current = idStr;
          return;
        }
        if (!applyFromLeadRow(data)) {
          leadContactTimingAutofillLeadIdRef.current = idStr;
        }
      } catch {
        leadContactTimingAutofillLeadIdRef.current = idStr;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isLeadContactTimingForm,
    isViewMode,
    mode,
    formData?.organization_ems_lead_id,
    selectOptions?.Leads,
    organizationId,
    token,
    setFormData,
  ]);

  useEffect(() => {
    if (!embeddedLeadActivityConfig || baseUrl !== "/api/ems/leads" || isViewMode) {
      setLeadActivityEmbed(null);
      setLeadActivityEmbedErrors({});
      setLeadActivityEmbedTouched(false);
      leadActivityEmbedHydratedRef.current = false;
      return;
    }
    const nextOrg =
      organizationId === null || organizationId === undefined
        ? ""
        : String(organizationId);
    setLeadActivityEmbed((prev) => {
      if (prev == null) {
        return {
          ...embeddedLeadActivityConfig.initialData,
          organization_id: nextOrg,
          organization_ems_lead_stage_id: formData?.lead_stage_id ?? "",
          activity_datetime:
            mode === "add"
              ? toDatetimeLocalInputValueEmbed(new Date())
              : embeddedLeadActivityConfig.initialData?.activity_datetime || "",
        };
      }
      return {
        ...prev,
        organization_id: nextOrg || prev.organization_id,
        organization_ems_lead_stage_id:
          prev?.organization_ems_lead_stage_id ??
          formData?.lead_stage_id ??
          "",
      };
    });
  }, [
    embeddedLeadActivityConfig,
    baseUrl,
    isViewMode,
    organizationId,
    formData?.lead_stage_id,
    mode,
  ]);

  useEffect(() => {
    if (baseUrl !== "/api/ems/leads" || mode !== "add" || isViewMode) return;
    if (leadActivityEmbedTouched) return;
    const leadDatetime = formData?.lead_datetime;
    if (!leadDatetime) return;
    setLeadActivityEmbed((prev) => {
      if (!prev) return prev;
      if (prev.activity_datetime === leadDatetime) return prev;
      return { ...prev, activity_datetime: leadDatetime };
    });
  }, [
    baseUrl,
    formData?.lead_datetime,
    isViewMode,
    leadActivityEmbedTouched,
    mode,
  ]);

  useEffect(() => {
    if (baseUrl !== "/api/ems/leads" || !embeddedLeadActivityConfig || isViewMode) {
      return;
    }
    const activityDt = leadActivityEmbed?.activity_datetime;
    const leadDt = formData?.lead_datetime;
    if (!activityDt || !leadDt) return;
    const msg = validateActivityDatetimeAgainstLead(activityDt, leadDt);
    setLeadActivityEmbedErrors((prev) => {
      const nextMsg = msg || "";
      if ((prev?.activity_datetime || "") === nextMsg) return prev;
      return { ...prev, activity_datetime: nextMsg };
    });
  }, [
    baseUrl,
    embeddedLeadActivityConfig,
    formData?.lead_datetime,
    isViewMode,
    leadActivityEmbed?.activity_datetime,
  ]);

  useEffect(() => {
    if (baseUrl !== "/api/ems/lead-activities" || isViewMode) return;
    const leadId = formData?.organization_ems_lead_id;
    if (!leadId) {
      if (formData?._lead_datetime) {
        setFormData((prev) => ({ ...prev, _lead_datetime: "" }));
      }
      return;
    }
    const leads = selectOptions?.Leads || [];
    const lead = leads.find((l) =>
      isSameValue(l?.organization_ems_lead_id, leadId)
    );
    const normalized = apiDatetimeToDatetimeLocalEmbed(lead?.lead_datetime);
    if (normalized) {
      if (formData?._lead_datetime !== normalized) {
        setFormData((prev) => ({ ...prev, _lead_datetime: normalized }));
      }
      const msg = validateActivityDatetimeAgainstLead(
        formData?.activity_datetime,
        normalized
      );
      setFormErrors((prev) => {
        const nextMsg = msg || "";
        if ((prev?.activity_datetime || "") === nextMsg) return prev;
        return { ...prev, activity_datetime: nextMsg };
      });
      return;
    }
    if (!formData?._lead_datetime) {
      void applyLeadActivityLeadConstraint(
        leadId,
        lead,
        formData?.activity_datetime
      );
    }
  }, [
    applyLeadActivityLeadConstraint,
    baseUrl,
    formData?._lead_datetime,
    formData?.activity_datetime,
    formData?.organization_ems_lead_id,
    isViewMode,
    selectOptions?.Leads,
    setFormData,
  ]);

  useEffect(() => {
    if (baseUrl !== "/api/ems/lead-activities" || isViewMode) return;
    const activityDt = formData?.activity_datetime;
    const leadDt = formData?._lead_datetime;
    if (!activityDt || !leadDt) return;
    const msg = validateActivityDatetimeAgainstLead(activityDt, leadDt);
    setFormErrors((prev) => {
      const nextMsg = msg || "";
      if ((prev?.activity_datetime || "") === nextMsg) return prev;
      return { ...prev, activity_datetime: nextMsg };
    });
  }, [
    baseUrl,
    formData?._lead_datetime,
    formData?.activity_datetime,
    isViewMode,
  ]);

  useEffect(() => {
    if (!embeddedLeadActivityConfig || baseUrl !== "/api/ems/leads") return;
    if (mode !== "edit" && mode !== "view") return;
    if (leadActivityEmbedTouched) return;
    if (leadActivityEmbedHydratedRef.current) return;
    const list = Array.isArray(formData?.lead_activities)
      ? formData.lead_activities
      : Array.isArray(formData?.leadActivities)
        ? formData.leadActivities
        : [];
    if (!list.length) return;
    const latest = [...list]
      .filter((x) => x && typeof x === "object")
      .sort((a, b) => {
        const ta = Date.parse(a?.updated_at || a?.created_at || a?.activity_datetime || 0);
        const tb = Date.parse(b?.updated_at || b?.created_at || b?.activity_datetime || 0);
        return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      })[0];
    if (!latest) return;

    setLeadActivityEmbed((prev) => ({
      ...(prev || embeddedLeadActivityConfig.initialData),
      organization_ems_lead_activity_id:
        latest?.organization_ems_lead_activity_id ??
        prev?.organization_ems_lead_activity_id ??
        "",
      organization_id:
        prev?.organization_id ??
        formData?.organization_id ??
        organizationId ??
        "",
      activity_datetime: latest?.activity_datetime
        ? String(latest.activity_datetime).slice(0, 16).replace(" ", "T")
        : prev?.activity_datetime || "",
      lead_direction: latest?.lead_direction ?? prev?.lead_direction ?? "",
      activity_type: latest?.activity_type ?? prev?.activity_type ?? "",
      call_status: latest?.call_status ?? prev?.call_status ?? "",
      person_type_id:
        latest?.organization_user_type_id ??
        latest?.trainer?.organization_user_type_id ??
        latest?.trainer?.organization_user?.organization_user_type_id ??
        latest?.person_type_id ??
        latest?.trainer?.person_type_id ??
        latest?.trainer?.organization_person?.person_type_id ??
        prev?.person_type_id ??
        "",
      organization_user_id:
        latest?.organization_user_id ??
        latest?.trainer?.organization_user_id ??
        latest?.trainer?.organization_user?.organization_user_id ??
        latest?.organization_person_id ??
        latest?.trainer?.organization_person_id ??
        prev?.organization_user_id ??
        "",
      organization_ems_lead_stage_id:
        latest?.organization_ems_lead_stage_id ??
        latest?.lead_stage?.organization_ems_lead_stage_id ??
        prev?.organization_ems_lead_stage_id ??
        "",
    }));
    const hydratedPersonType =
      latest?.organization_user_type_id ??
      latest?.trainer?.organization_user_type_id ??
      latest?.trainer?.organization_user?.organization_user_type_id ??
      latest?.person_type_id ??
      latest?.trainer?.person_type_id ??
      latest?.trainer?.organization_person?.person_type_id;
    if (
      hydratedPersonType !== undefined &&
      hydratedPersonType !== null &&
      String(hydratedPersonType).trim() !== ""
    ) {
      setPersonTypeId(String(hydratedPersonType));
    }
    leadActivityEmbedHydratedRef.current = true;
  }, [
    embeddedLeadActivityConfig,
    baseUrl,
    mode,
    formData?.lead_activities,
    formData?.leadActivities,
    formData?.organization_id,
    organizationId,
    leadActivityEmbedTouched,
  ]);

  useEffect(() => {
    demoParticipantsHydratedRef.current = false;
  }, [demoSessionEditId]);

  const createParticipantFormData = useMemo(
    () => () => ({
      organization_id: organizationId || "",
      ...initialData,
    }),
    [initialData, organizationId]
  );

  const [participantForms, setParticipantForms] = useState([]);
  const [participantBtnLoading, setParticipantBtnLoading] = useState(false);
  const [expandedParticipantKey, setExpandedParticipantKey] = useState(null);
  const userTypeOptions = useMemo(() => {
    const explicit = selectOptions?.UserTypes;
    if (Array.isArray(explicit) && explicit.length > 0) {
      return explicit
        .map((x) => ({
          value: String(
            x?.organization_user_type_id ??
              x?.user_type_id ??
              x?.person_type_id ??
              x?.user_type ??
              x?.application_user_type ??
              x?.id ??
              x?.value ??
              x?.name ??
              ""
          ).trim(),
          label: String(
            x?.label ??
              x?.user_type_name ??
              x?.name ??
              x?.user_type ??
              x?.application_user_type ??
              x?.value ??
              ""
          ).trim(),
        }))
        .filter((x) => x.value && x.label);
    }
    const inferred = [
      ...(Array.isArray(selectOptions?.Employees) ? selectOptions.Employees : []),
      ...(Array.isArray(selectOptions?.Trainers) ? selectOptions.Trainers : []),
    ];
    const seen = new Set();
    return inferred
      .map((x) =>
        String(
          x?.user_type_label ??
            x?.user_type_name ??
            x?.user_type ??
            x?.organization_user?.user_type_name ??
            x?.application_user?.user_type ??
            x?.application_user?.user_type_name ??
            x?.application_user?.application_user_type ??
            ""
        ).trim()
      )
      .filter(Boolean)
      .filter((v) => {
        const k = v.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .map((v) => ({ value: v, label: v }));
  }, [selectOptions]);
  const effectiveUserTypeOptions = useMemo(() => {
    if (Array.isArray(userTypeOptions) && userTypeOptions.length > 0) {
      return userTypeOptions;
    }
    return [
      { value: "Employee", label: "Employee" },
      { value: "Interns", label: "Interns" },
    ];
  }, [userTypeOptions]);
  const leadActivityUserTypeOptions = useMemo(() => {
    const normalize = (v) =>
      String(v || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
    const allowed = new Set([
      "employee",
      "shared resource",
      "shared resources",
      "intern",
      "interns",
    ]);
    const opts = Array.isArray(effectiveUserTypeOptions) ? effectiveUserTypeOptions : [];
    const filtered = opts.filter((opt) => {
      const n = normalize(opt?.label || opt?.value);
      return allowed.has(n);
    });
    // If no known types match, show all available types so the field is never empty.
    return filtered.length > 0 ? filtered : opts;
  }, [effectiveUserTypeOptions]);

  const [embedParticipantForms, setEmbedParticipantForms] = useState([]);
  const [embedExpandedParticipantKey, setEmbedExpandedParticipantKey] =
    useState(null);

  useEffect(() => {
    embedParticipantFormsRef.current = embedParticipantForms;
  }, [embedParticipantForms]);

  useEffect(() => {
    if (!isDemoParticipantsAddMode) {
      setParticipantForms([]);
      return;
    }
    setParticipantForms([
      {
        key: Date.now(),
        formData: createParticipantFormData(),
        formErrors: {},
      },
    ]);
    setExpandedParticipantKey(null);
  }, [isDemoParticipantsAddMode, createParticipantFormData]);

  // Demo Participants add mode uses local accordion state.
  // Sync query-prefilled values (from useFormHandler/formData) into first accordion form.
  useEffect(() => {
    if (!isDemoParticipantsAddMode) return;
    if (!participantForms.length) return;

    const demoSessionId = formData?.organization_ems_demo_session_id;
    const leadId = formData?.organization_ems_lead_id;
    if (!demoSessionId && !leadId) return;

    setParticipantForms((prev) => {
      if (!prev.length) return prev;
      const first = prev[0];
      const nextFirstData = {
        ...first.formData,
        ...(demoSessionId
          ? { organization_ems_demo_session_id: demoSessionId }
          : null),
        ...(leadId ? { organization_ems_lead_id: leadId } : null),
      };
      const noChange =
        isSameValue(
          first?.formData?.organization_ems_demo_session_id,
          nextFirstData.organization_ems_demo_session_id
        ) &&
        isSameValue(
          first?.formData?.organization_ems_lead_id,
          nextFirstData.organization_ems_lead_id
        );
      if (noChange) return prev;
      const next = [...prev];
      next[0] = { ...first, formData: nextFirstData };
      return next;
    });
  }, [
    isDemoParticipantsAddMode,
    participantForms.length,
    formData?.organization_ems_demo_session_id,
    formData?.organization_ems_lead_id,
  ]);

  useEffect(() => {
    if (!isDemoParticipantsAddMode) return;
    if (!participantForms.length) return;
    if (expandedParticipantKey != null) return;
    setExpandedParticipantKey(participantForms[0].key);
  }, [
    isDemoParticipantsAddMode,
    participantForms,
    expandedParticipantKey,
  ]);

  const buildCurrencyFeeRows = (currencies = [], existingRows = []) => {
    const existingMap = new Map(
      (Array.isArray(existingRows) ? existingRows : [])
        .filter((row) => row?.currency_code)
        .map((row) => [
          String(row.currency_code),
          {
            currency_code: String(row.currency_code),
            currency_name: row.currency_name || "",
            amount:
              row.amount === null || row.amount === undefined
                ? ""
                : String(row.amount),
          },
        ])
    );

    const normalized = (Array.isArray(currencies) ? currencies : []).map((c) => {
      const code = String(c?.currency_code || "").trim();
      const existing = existingMap.get(code);
      return {
        currency_code: code,
        currency_name: c?.currency_name || existing?.currency_name || "",
        amount: existing?.amount ?? "",
      };
    });

    existingMap.forEach((row, code) => {
      if (!normalized.some((n) => n.currency_code === code)) normalized.push(row);
    });

    return normalized.filter((row) => row.currency_code);
  };

  const resolveProgramFeeRows = (program) => {
    if (!program) return [];
    if (Array.isArray(program.fee_structures)) return program.fee_structures;
    if (Array.isArray(program.feeStructures)) return program.feeStructures;
    if (program.fee_structure) return [program.fee_structure];
    if (program.feeStructure) return [program.feeStructure];
    return [];
  };

  const getTrainingProgramFeeAmount = (feeRows, code) => {
    const normalizedCode = String(code || "").trim().toUpperCase();
    if (!normalizedCode) return null;

    const row = feeRows.find(
      (r) =>
        String(r?.currency_code || "").trim().toUpperCase() === normalizedCode
    );
    if (!row) return null;

    const amount = row?.amount;
    if (amount === "" || amount === null || amount === undefined) return null;

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) return null;

    return amount;
  };

  const hasValidTrainingProgramFee = (feeRows, code, program) => {
    if (getTrainingProgramFeeAmount(feeRows, code) !== null) return true;

    if (String(code || "").trim().toUpperCase() === "INR") {
      const legacyInr = program?.fees_in_inr;
      if (legacyInr !== null && legacyInr !== undefined && legacyInr !== "") {
        const numericInr = Number(legacyInr);
        return Number.isFinite(numericInr) && numericInr > 0;
      }
    }

    return false;
  };

  const getTrainingProgramAmountByCode = (feeRows, code, program) => {
    const amount = getTrainingProgramFeeAmount(feeRows, code);
    if (amount !== null) return amount;

    if (String(code || "").trim().toUpperCase() === "INR") {
      const legacyInr = program?.fees_in_inr;
      if (legacyInr !== null && legacyInr !== undefined && legacyInr !== "") {
        return legacyInr;
      }
    }

    return "";
  };

  // Check if Employees or Trainers are in DataFetchers
  const hasEmployeesOrTrainers = DataFetchers?.some(
    (fetcher) => (fetcher.name === "Employees" || fetcher.name === "Trainers") && fetcher.Type === "Fetch"
  );

  // User Types come from organization user-types model (source of truth for type dropdown).
  useEffect(() => {
    if (!hasEmployeesOrTrainers || !organizationId || !token) return;

    const hasExplicitUserTypeFetcher = DataFetchers?.some(
      (f) => f?.name === "UserTypes"
    );
    if (hasExplicitUserTypeFetcher && Array.isArray(selectOptions?.UserTypes) && selectOptions.UserTypes.length > 0) {
      return;
    }

    const fetchUserTypes = async () => {
      try {
        const res = await axios.get(
          `${MAIN_URL}/api/organizations/${organizationId}/user-type`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { per_page: "all", organization_id: organizationId },
          }
        );
        const userTypes = normalizeApiArray(res);
        setSelectOptions((prev) => ({
          ...prev,
          UserTypes: Array.isArray(userTypes) ? userTypes : [],
        }));
      } catch (err) {
        console.error("Failed to fetch user types:", err);
      }
    };

    fetchUserTypes();
  }, [hasEmployeesOrTrainers, organizationId, token, DataFetchers]);

  // Fetch employees/trainers when type is selected (person/user type)
  const fetchEmployeesOrTrainers = async (personType) => {
    if (!personType) {
      // Clear employees/trainers if no person type selected
      DataFetchers?.forEach((fetcher) => {
        if ((fetcher.name === "Employees" || fetcher.name === "Trainers") && fetcher.Type === "Fetch") {
          setSelectOptions((prev) => ({ ...prev, [fetcher.name]: [] }));
        }
      });
      return;
    }

    try {
      const results = await Promise.all(
        DataFetchers?.map(async (fetcher) => {
          if (fetcher.Type === "Fetch" && (fetcher.name === "Employees" || fetcher.name === "Trainers")) {
            const dynamicLink = replaceOrgIdInApiLink(fetcher.link, organizationId);
            
            const params = {
              per_page: "all",
              organization_id: organizationId,
            };
            const selectedType = String(personType || "").trim();
            if (selectedType) {
              if (/^\d+$/.test(selectedType)) params.organization_user_type_id = selectedType;
              else params.user_type = selectedType;
            }
            const res = await axios.get(`${MAIN_URL}${dynamicLink}`, {
              headers: { Authorization: `Bearer ${token}` },
              params,
            });
            let data = normalizeApiArray(res);
            data = data.map((item) => normalizeTrainerEmployeeOption(item));
            
            const selectedTypeLower = String(personType || "").trim().toLowerCase();
            if (selectedTypeLower) {
              if (/^\d+$/.test(selectedTypeLower)) {
                const personTypeNum = parseInt(selectedTypeLower, 10);
                data = data.filter((item) => {
                  const candidateTypeIds = [
                    item?.organization_user_type_id,
                    item?.organization_user?.organization_user_type_id,
                    item?.person_type_id,
                    item?.organization_person?.person_type_id,
                  ]
                    .map((v) => (v == null || v === "" ? null : parseInt(v, 10)))
                    .filter((v) => Number.isInteger(v));
                  return candidateTypeIds.includes(personTypeNum);
                });
              } else {
                data = data.filter((item) => {
                  const itemType = String(
                    item?.user_type_label ??
                      item?.user_type_name ??
                      item?.user_type ??
                      item?.application_user?.user_type_name ??
                      item?.application_user?.user_type ??
                      item?.application_user?.application_user_type ??
                      item?.organization_user?.user_type_name ??
                      item?.organization_user?.user_type ??
                      ""
                  )
                    .trim()
                    .toLowerCase();
                  return itemType === selectedTypeLower;
                });
              }
            }
            
            return { [fetcher.name]: data };
          }
          return null;
        })
      );

      const merged = results.filter(Boolean).reduce((acc, cur) => ({ ...acc, ...cur }), {});
      setSelectOptions((prev) => ({ ...prev, ...merged }));
    } catch (err) {
      console.error("Error fetching Employees/Trainers:", err);
    }
  };

  useEffect(() => {
    if (personTypeId) {
      fetchEmployeesOrTrainers(personTypeId);
    } else {
      // Clear employees/trainers when person type is cleared
      DataFetchers?.forEach((fetcher) => {
        if ((fetcher.name === "Employees" || fetcher.name === "Trainers") && fetcher.Type === "Fetch") {
          setSelectOptions((prev) => ({ ...prev, [fetcher.name]: [] }));
        }
      });
    }
  }, [personTypeId]);

  // Extract person_type_id from trainer/employee data in edit mode
  useEffect(() => {
    const shouldSyncPersonType =
      (mode === "edit" || mode === "view") &&
      hasEmployeesOrTrainers &&
      formData &&
      !personTypeId;

    if (!shouldSyncPersonType) return;

    // Check for trainer field (trainer_employee_id)
    // The trainer relationship returns an OrganizationPerson which has person_type_id directly
    if (formData.trainer_employee_id) {
      const trainerTypeValue =
        formData?.trainer?.user_type_name ??
        formData?.trainer?.user_type ??
        formData?.trainer?.organization_user_type_id ??
        formData?.trainer?.application_user?.user_type ??
        formData?.trainer?.application_user?.user_type_name ??
        formData?.trainer?.application_user?.application_user_type ??
        formData?.trainer?.person_type_id;
      if (trainerTypeValue) {
        setPersonTypeId(String(trainerTypeValue));
        return;
      }
    }
    // employee/user field: some resources eager-load `employee`, others use `trainer`
    if (formData.organization_user_id || formData.organization_person_id) {
      const personTypeFromRelation =
        formData?.employee?.user_type_name ??
        formData?.employee?.user_type ??
        formData?.employee?.organization_user_type_id ??
        formData?.employee?.application_user?.user_type ??
        formData?.employee?.application_user?.user_type_name ??
        formData?.employee?.application_user?.application_user_type ??
        formData?.trainer?.user_type_name ??
        formData?.trainer?.user_type ??
        formData?.trainer?.organization_user_type_id ??
        formData?.trainer?.application_user?.user_type ??
        formData?.trainer?.application_user?.user_type_name ??
        formData?.trainer?.application_user?.application_user_type ??
        formData.employee?.person_type_id ??
        formData.trainer?.person_type_id;
      if (personTypeFromRelation) {
        setPersonTypeId(String(personTypeFromRelation));
        return;
      }

      // Fallback: derive type by matching selected user id with fetched Employees/Trainers options.
      const selectedId = String(
        formData.organization_user_id ?? formData.organization_person_id ?? ""
      ).trim();
      if (!selectedId) return;
      const pool = [
        ...(Array.isArray(selectOptions?.Employees) ? selectOptions.Employees : []),
        ...(Array.isArray(selectOptions?.Trainers) ? selectOptions.Trainers : []),
      ];
      const matched = pool.find((opt) => {
        const candidateIds = [
          opt?.organization_user_id,
          opt?.organization_user?.organization_user_id,
          opt?.organization_person_id,
          opt?.organization_person?.organization_person_id,
        ]
          .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
          .map((v) => String(v).trim());
        return candidateIds.includes(selectedId);
      });
      const matchedType =
        matched?.organization_user_type_id ??
        matched?.organization_user?.organization_user_type_id ??
        matched?.person_type_id ??
        matched?.organization_person?.person_type_id ??
        matched?.user_type_name ??
        matched?.user_type ??
        matched?.application_user?.user_type_name ??
        matched?.application_user?.user_type ??
        matched?.application_user?.application_user_type;
      if (matchedType) {
        setPersonTypeId(String(matchedType));
      }
    }
  }, [mode, formData, hasEmployeesOrTrainers, personTypeId, selectOptions]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const results = await Promise.all(
          DataFetchers?.map(async (fetcher) => {
            // FetchDepend options are loaded by dependency-driven effect.
            // Returning null here avoids wiping already-fetched dependent values.
            if (fetcher.Type === "FetchDepend") {
              return null;
            }

            // Skip Employees and Trainers - they will be fetched when person_type_id is selected
            if (fetcher.Type === "Fetch" && (fetcher.name === "Employees" || fetcher.name === "Trainers")) {
              return { [fetcher.name]: [] };
            }
            
            if (fetcher.Type === "Fetch") {
              const dynamicLink = replaceOrgIdInApiLink(fetcher.link, organizationId);
              
              const res = await axios.get(`${MAIN_URL}${dynamicLink}`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { per_page: "all", organization_id: organizationId },
              });
              let data = normalizeApiArray(res);
              if (
                baseUrl === "/api/ems/class-records" &&
                fetcher.name === "Batches" &&
                canViewSelfBatchClassRecords &&
                !canViewAllBatchClassRecords
              ) {
                data = (Array.isArray(data) ? data : []).filter((batch) => {
                  const classes = Array.isArray(batch?.batch_classes)
                    ? batch.batch_classes
                    : [];
                  return classes.some((cls) =>
                    emsRowMatchesLoggedInTrainer(
                      {
                        trainer: cls?.trainer,
                        trainer_employee_id: cls?.trainer_employee_id,
                      },
                      userData
                    )
                  );
                });
              }
              if (
                baseUrl === "/api/ems/classes-attendance" &&
                canViewSelfClassAttendance &&
                !canViewAllClassAttendance
              ) {
                if (fetcher.name === "BatchClasses") {
                  data = (Array.isArray(data) ? data : []).filter((batchClass) =>
                    emsRowMatchesLoggedInTrainer(batchClass, userData)
                  );
                } else if (fetcher.name === "ClassRecords") {
                  data = (Array.isArray(data) ? data : []).filter((record) =>
                    emsRowMatchesLoggedInTrainer(
                      {
                        trainer:
                          record?.trainer ??
                          record?.batchClass?.trainer ??
                          record?.batch_class?.trainer,
                        trainer_employee_id:
                          record?.trainer_employee_id ??
                          record?.batchClass?.trainer_employee_id ??
                          record?.batch_class?.trainer_employee_id,
                      },
                      userData
                    )
                  );
                }
              }
              if (fetcher.name === "TrainingSpaces" && Array.isArray(data)) {
                data = data.map((row) => ({
                  ...row,
                  ems_training_space_id:
                    row?.ems_training_space_id ??
                    row?.organization_ems_training_space_id ??
                    row?.id,
                }));
              }
              if (
                fetcher.name === "Leads" &&
                baseUrl === "/api/ems/students" &&
                Array.isArray(data)
              ) {
                data = filterLeadsForStudentEnrollment(data, {
                  keepLeadId: formData?.organization_ems_lead_id,
                });
              }

              return { [fetcher.name]: data };
            } else if (fetcher.Type === "NoFetch") {
              return { [fetcher.name]: fetcher.Options || [] };
            } else {
              return { [fetcher.name]: [] };
            }
          })
        );

        const merged = results
          .filter(Boolean)
          .reduce((acc, cur) => ({ ...acc, ...cur }), {});
        setSelectOptions((prev) => ({ ...prev, ...merged }));
      } catch (err) {
        console.error("Error fetching DataFetchers:", err);
      }
    };

    // Trigger option fetches as soon as we know the organization id.
    // Use `!== null/undefined` because some payload shapes may yield "" temporarily.
    if (
      DataFetchers?.length > 0 &&
      token &&
      organizationId !== null &&
      organizationId !== undefined
    ) {
      fetchInitialData();
    }
  }, [
    DataFetchers,
    token,
    organizationId,
    baseUrl,
    canViewSelfBatchClassRecords,
    canViewAllBatchClassRecords,
    canViewSelfClassAttendance,
    canViewAllClassAttendance,
    userData,
  ]);

  useEffect(() => {
    if (!isTrainingProgramForm) return;
    const currencyRows = selectOptions?.Currencies || [];
    if (!currencyRows.length) return;

    const mergedRows = buildCurrencyFeeRows(currencyRows, formData?.fee_structures || []);
    const currentRows = Array.isArray(formData?.fee_structures) ? formData.fee_structures : [];
    const same =
      mergedRows.length === currentRows.length &&
      mergedRows.every((row, idx) =>
        row.currency_code === currentRows[idx]?.currency_code &&
        String(row.amount ?? "") === String(currentRows[idx]?.amount ?? "") &&
        String(row.currency_name ?? "") === String(currentRows[idx]?.currency_name ?? "")
      );
    if (!same) {
      setFormData((prev) => ({ ...prev, fee_structures: mergedRows }));
    }
  }, [isTrainingProgramForm, selectOptions?.Currencies, formData?.fee_structures, setFormData]);

  // Future-dated batches may only be Scheduled or Cancelled — clear invalid status
  useEffect(() => {
    if (baseUrl !== "/api/ems/batches") return;
    const { start_date, end_date, status } = formData || {};
    if (!start_date || !end_date || !status) return;
    if (status === "Scheduled" || status === "Cancelled") return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(start_date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(end_date);
    end.setHours(0, 0, 0, 0);
    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime()) ||
      start <= today ||
      end <= today
    ) {
      return;
    }

    setFormData((prev) => ({ ...prev, status: "" }));
    setFormErrors((prev) => ({
      ...prev,
      status:
        "When start and end dates are in the future, status can only be Scheduled or Cancelled.",
    }));
  }, [
    baseUrl,
    formData?.start_date,
    formData?.end_date,
    formData?.status,
    setFormData,
    setFormErrors,
  ]);

  useEffect(() => {
    if (baseUrl !== "/api/ems/assesment-results") return;

    const assessmentId = formData?.organization_ems_assessment_id;
    if (!assessmentId) {
      if (formData?.assessment_max_score !== "") {
        setFormData((prev) => ({
          ...prev,
          assessment_max_score: "",
        }));
      }
      return;
    }

    const assessments = Array.isArray(selectOptions?.Assesments)
      ? selectOptions.Assesments
      : [];
    const selectedAssessment = assessments.find((a) =>
      isSameValue(a?.organization_ems_assessment_id, assessmentId)
    );
    if (!selectedAssessment) return;

    const selectedMaxScore = selectedAssessment?.max_score;
    if (
      !isSameValue(
        formData?.assessment_max_score ?? "",
        selectedMaxScore ?? ""
      )
    ) {
      setFormData((prev) => ({
        ...prev,
        assessment_max_score: selectedMaxScore ?? "",
      }));
    }

    const batchId =
      selectedAssessment?.organization_ems_batch_id ??
      selectedAssessment?.batch?.organization_ems_batch_id;
    if (!batchId) return;

    const batchStudents = Array.isArray(selectOptions?.BatchStudents)
      ? selectOptions.BatchStudents
      : [];

    const allowedStudentIds = new Set(
      batchStudents
        .filter((row) =>
          isSameValue(
            row?.organization_ems_batch_id ?? row?.batch?.organization_ems_batch_id,
            batchId
          )
        )
        .map(
          (row) =>
            row?.organization_ems_student_id ??
            row?.student?.organization_ems_student_id
        )
        .filter((sid) => sid != null && String(sid).trim() !== "")
        .map((sid) => String(sid))
    );

    if (!allowedStudentIds.size) return;
    if (
      !formData?.organization_ems_student_id ||
      allowedStudentIds.has(String(formData.organization_ems_student_id))
    ) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      organization_ems_student_id: "",
      organization_ems_admission_id: "",
    }));
  }, [
    baseUrl,
    formData?.organization_ems_assessment_id,
    formData?.organization_ems_student_id,
    selectOptions?.Assesments,
    selectOptions?.BatchStudents,
    setFormData,
  ]);

  useEffect(() => {
    if (baseUrl !== "/api/ems/assesment-results") return;

    const assessmentId = formData?.organization_ems_assessment_id;
    const scoreRaw = formData?.score_obtained;
    if (!assessmentId || scoreRaw === "" || scoreRaw == null) return;

    const assessments = Array.isArray(selectOptions?.Assesments)
      ? selectOptions.Assesments
      : [];
    const selectedAssessment = assessments.find((a) =>
      isSameValue(a?.organization_ems_assessment_id, assessmentId)
    );
    if (!selectedAssessment) return;

    const passingScore = Number(selectedAssessment?.passing_score);
    const obtainedScore = Number(scoreRaw);
    if (!Number.isFinite(passingScore) || !Number.isFinite(obtainedScore)) return;

    const nextStatus = obtainedScore >= passingScore ? "Pass" : "Fail";
    if (isSameValue(formData?.result_status, nextStatus)) return;

    setFormData((prev) =>
      isSameValue(prev?.result_status, nextStatus)
        ? prev
        : { ...prev, result_status: nextStatus }
    );
  }, [
    baseUrl,
    formData?.organization_ems_assessment_id,
    formData?.score_obtained,
    formData?.result_status,
    selectOptions?.Assesments,
    setFormData,
  ]);

  useEffect(() => {
    if (!isAdmissionsForm) return;
    if (!formData?.training_program_id) return;

    const programs = Array.isArray(selectOptions?.TrainingPrograms)
      ? selectOptions.TrainingPrograms
      : [];
    if (!programs.length) return;

    const selectedProgram = programs.find((p) =>
      isSameValue(
        p?.organization_ems_training_program_id ?? p?.training_program_id,
        formData?.training_program_id
      )
    );
    if (!selectedProgram) return;

    const feeRows = resolveProgramFeeRows(selectedProgram);

    const inrAmount = getTrainingProgramAmountByCode(
      feeRows,
      "INR",
      selectedProgram
    );
    // null = no currency selected (leave the field alone);
    // ""   = currency selected but no matching fee (clear the field).
    const selectedCurrencyAmount = formData?.currency_code
      ? getTrainingProgramAmountByCode(
          feeRows,
          formData.currency_code,
          selectedProgram
        )
      : null;

    const currentProgramId = formData?.training_program_id;
    const programChanged =
      prevAdmissionTrainingProgramIdRef.current !== null &&
      !isSameValue(
        prevAdmissionTrainingProgramIdRef.current,
        currentProgramId
      );
    prevAdmissionTrainingProgramIdRef.current = currentProgramId;

    setFormData((prev) => {
      const updates = {};

      if (programChanged) {
        updates.discount_amount = "";
        updates.discount_reason = "";
        updates.discount_amount_student_currency = "";
        updates.discount_reason_student_currency = "";
      }

      // INR total fee: always sync when training program is selected.
      // If the program has no INR fee, clear the field.
      if (!isSameValue(prev?.total_fee_amount, inrAmount)) {
        updates.total_fee_amount = inrAmount;
      }

      const currentDiscount = programChanged
        ? 0
        : Number(prev?.discount_amount || 0);

      // INR net fee: reset to full fee when program changes, or when no discount.
      if (
        programChanged ||
        (currentDiscount === 0 && !isSameValue(prev?.net_fee_amount, inrAmount))
      ) {
        updates.net_fee_amount = inrAmount;
      }

      // Student-currency total fee: sync whenever a currency code is selected.
      // If the new program has no fee for that currency, clear the field.
      if (
        selectedCurrencyAmount !== null &&
        !isSameValue(
          prev?.total_fee_amount_student_currency,
          selectedCurrencyAmount
        )
      ) {
        updates.total_fee_amount_student_currency = selectedCurrencyAmount;
      }

      const inrTotalNum = Number(inrAmount);
      const studentTotalNum = Number(selectedCurrencyAmount);
      if (
        selectedCurrencyAmount !== null &&
        selectedCurrencyAmount !== "" &&
        Number.isFinite(inrTotalNum) &&
        inrTotalNum > 0 &&
        Number.isFinite(studentTotalNum)
      ) {
        if (programChanged) {
          updates.discount_amount_student_currency = "";
          updates.net_fee_amount_student_currency = String(studentTotalNum);
        } else {
          const studentDiscount =
            Math.round(
              ((currentDiscount / inrTotalNum) * studentTotalNum) * 100
            ) / 100;
          const studentNet = Math.max(studentTotalNum - studentDiscount, 0);

          if (
            !isSameValue(prev?.discount_amount_student_currency, studentDiscount)
          ) {
            updates.discount_amount_student_currency = String(studentDiscount);
          }
          if (
            !isSameValue(prev?.net_fee_amount_student_currency, studentNet)
          ) {
            updates.net_fee_amount_student_currency = String(studentNet);
          }
        }
      } else if (selectedCurrencyAmount !== null && selectedCurrencyAmount === "") {
        if (!isSameValue(prev?.discount_amount_student_currency, "")) {
          updates.discount_amount_student_currency = "";
        }
        if (!isSameValue(prev?.net_fee_amount_student_currency, "")) {
          updates.net_fee_amount_student_currency = "";
        }
      } else if (
        selectedCurrencyAmount !== null &&
        (programChanged || currentDiscount === 0) &&
        !isSameValue(
          prev?.net_fee_amount_student_currency,
          selectedCurrencyAmount
        )
      ) {
        updates.net_fee_amount_student_currency = selectedCurrencyAmount;
      }

      return Object.keys(updates).length ? { ...prev, ...updates } : prev;
    });
  }, [
    baseUrl,
    formData?.training_program_id,
    formData?.currency_code,
    selectOptions?.TrainingPrograms,
    setFormData,
  ]);

  useEffect(() => {
    if (!isAdmissionsForm) return;
    const currencyCode = String(formData?.currency_code || "").trim().toUpperCase();
    if (!currencyCode) return;
    if (!formData?.training_program_id) return;

    const programs = Array.isArray(selectOptions?.TrainingPrograms)
      ? selectOptions.TrainingPrograms
      : [];
    if (!programs.length) return;

    const selectedProgram = programs.find((p) =>
      isSameValue(
        p?.organization_ems_training_program_id ?? p?.training_program_id,
        formData.training_program_id
      )
    );
    if (!selectedProgram) return;

    const feeRows = resolveProgramFeeRows(selectedProgram);
    const hasFee = hasValidTrainingProgramFee(
      feeRows,
      currencyCode,
      selectedProgram
    );

    if (!hasFee) {
      const feeRowsSignature = feeRows
        .map(
          (row) =>
            `${String(row?.currency_code || "").trim().toUpperCase()}:${row?.amount ?? ""}`
        )
        .join("|");
      const warnKey = `${formData.training_program_id}::${currencyCode}::${feeRowsSignature}`;
      if (admissionMissingFeePopupRef.current === warnKey) return;
      admissionMissingFeePopupRef.current = warnKey;

      const programId =
        selectedProgram?.organization_ems_training_program_id ??
        selectedProgram?.training_program_id;
      const programName =
        selectedProgram?.training_program_name ?? "this training program";
      Swal.fire({
        icon: "warning",
        title: "Fee Not Defined",
        text: `No ${currencyCode} fee is set for "${programName}". Do you want to go to the Training Program and add it?`,
        showCancelButton: true,
        confirmButtonText: "Go to Training Program",
        cancelButtonText: "Stay Here",
        confirmButtonColor: "#1976d2",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate(`/training-programs/edit/${programId}`);
        }
      });
    }
  }, [
    isAdmissionsForm,
    formData?.currency_code,
    formData?.training_program_id,
    selectOptions?.TrainingPrograms,
    navigate,
  ]);

  useEffect(() => {
    if (!isAdmissionsForm) return;

    setFormData((prev) => {
      const count = Number(prev?.installment_count || 0);
      const existing = Array.isArray(prev?.fee_installments)
        ? prev.fee_installments
        : [];

      if (!Number.isFinite(count) || count < 1) {
        return existing.length ? { ...prev, fee_installments: [] } : prev;
      }

      const rows = buildFeeInstallmentRows({
        netInr: prev?.net_fee_amount,
        netStudent: prev?.net_fee_amount_student_currency,
        count,
        currencyCode: prev?.currency_code || "",
        existingRows: existing,
        preserveAmounts: true,
      });

      const same =
        existing.length === rows.length &&
        rows.every((row, idx) => {
          const cur = existing[idx] || {};
          return (
            isSameValue(cur.installment_number, row.installment_number) &&
            isSameValue(cur.due_date, row.due_date) &&
            isSameValue(cur.fee_instalment_inr, row.fee_instalment_inr) &&
            isSameValue(cur.currency_code_inr, row.currency_code_inr) &&
            isSameValue(
              cur.fee_instalment_student_student_currency,
              row.fee_instalment_student_student_currency
            ) &&
            isSameValue(cur.student_currency_code, row.student_currency_code)
          );
        });

      return same ? prev : { ...prev, fee_installments: rows };
    });
  }, [
    isAdmissionsForm,
    formData?.installment_count,
    formData?.currency_code,
    formData?.net_fee_amount,
    formData?.net_fee_amount_student_currency,
    setFormData,
  ]);

  useEffect(() => {
    if (baseUrl !== "/api/ems/fee-installments") return;

    const studentId = formData?.organization_ems_student_id;
    const admissionId = formData?.organization_ems_admission_id;
    const allInstallments = Array.isArray(selectOptions?.FeeInstallments)
      ? selectOptions.FeeInstallments
      : [];

    const matchingInstallments = allInstallments.filter((row) => {
      if (!studentId || !isSameValue(row?.organization_ems_student_id, studentId)) {
        return false;
      }
      if (
        admissionId &&
        !isSameValue(row?.organization_ems_admission_id, admissionId)
      ) {
        return false;
      }
      return true;
    });

    setFormData((prev) => {
      const prevExisting = prev?.existing_fee_installments || [];
      const same =
        prevExisting.length === matchingInstallments.length &&
        prevExisting.every((row, idx) =>
          isSameValue(
            row?.organization_ems_fee_installment_id,
            matchingInstallments[idx]?.organization_ems_fee_installment_id
          )
        );
      if (same) return prev;
      return { ...prev, existing_fee_installments: matchingInstallments };
    });
  }, [
    baseUrl,
    formData?.organization_ems_student_id,
    formData?.organization_ems_admission_id,
    selectOptions?.FeeInstallments,
    setFormData,
  ]);

  useEffect(() => {
    feeInstallmentInitialStatusRef.current = null;
  }, [feeInstallmentEditId, baseUrl, mode]);

  useEffect(() => {
    if (baseUrl !== "/api/ems/fee-installments") {
      feeInstallmentInitialStatusRef.current = null;
      return;
    }

    const paidInstallmentIds = getPaidFeeReceiptInstallmentIds(
      selectOptions?.StudentFees
    );
    setFormData((prev) => {
      const next = { ...prev };
      let changed = false;

      if (prev._form_mode !== mode) {
        next._form_mode = mode;
        changed = true;
      }

      const prevPaidIds = Array.isArray(prev._paid_fee_receipt_installment_ids)
        ? prev._paid_fee_receipt_installment_ids.map((id) => String(id))
        : [];
      const nextPaidIds = paidInstallmentIds.map((id) => String(id));
      if (JSON.stringify(prevPaidIds) !== JSON.stringify(nextPaidIds)) {
        next._paid_fee_receipt_installment_ids = paidInstallmentIds;
        changed = true;
      }

      if (
        mode === "edit" &&
        prev.status != null &&
        String(prev.status).trim() !== "" &&
        feeInstallmentInitialStatusRef.current === null
      ) {
        feeInstallmentInitialStatusRef.current = String(prev.status);
        if (prev._initial_status !== feeInstallmentInitialStatusRef.current) {
          next._initial_status = feeInstallmentInitialStatusRef.current;
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [
    baseUrl,
    mode,
    formData?.status,
    selectOptions?.StudentFees,
    setFormData,
  ]);

  const guardFeeInstallmentStatusChange = useCallback(
    (nextStatus) => {
      if (baseUrl !== "/api/ems/fee-installments") return true;

      const installmentId =
        formData?.organization_ems_fee_installment_id ?? feeInstallmentEditId;
      const initialStatus =
        formData?._initial_status ?? feeInstallmentInitialStatusRef.current ?? "";
      const statusChangeError = validateFeeInstallmentStatusChange({
        status: nextStatus,
        initialStatus,
        mode,
        installmentId,
        paidInstallmentIds: formData?._paid_fee_receipt_installment_ids,
        studentFees: selectOptions?.StudentFees,
      });

      if (!statusChangeError) {
        setFormErrors((prev) => {
          if (!prev?.status) return prev;
          const { status, ...rest } = prev;
          return rest;
        });
        return true;
      }

      const isAlreadyPaidError =
        statusChangeError === FEE_INSTALLMENT_ALREADY_PAID_MSG;
      Swal.fire({
        icon: "warning",
        title: isAlreadyPaidError ? "Fee Already Paid" : "Fee Receipt Required",
        text: statusChangeError,
        confirmButtonColor: "#1976d2",
      });
      setFormErrors((prev) => ({
        ...prev,
        status: statusChangeError,
      }));
      return false;
    },
    [
      baseUrl,
      mode,
      formData?.organization_ems_fee_installment_id,
      formData?._initial_status,
      formData?._paid_fee_receipt_installment_ids,
      feeInstallmentEditId,
      selectOptions?.StudentFees,
      setFormErrors,
    ]
  );

  useEffect(() => {
    if (baseUrl !== "/api/ems/fee-installments") return;

    const admissionId = formData?.organization_ems_admission_id;
    const installmentNumber = Number(formData?.installment_number || 0);
    if (!admissionId || !Number.isFinite(installmentNumber) || installmentNumber < 1) {
      return;
    }

    const admission = (selectOptions?.Admissions || []).find((row) =>
      isSameValue(row?.organization_ems_admission_id, admissionId)
    );
    if (!admission) return;

    const netInr = Number(admission?.net_fee_amount ?? 0);
    const netStudent = Number(admission?.net_fee_amount_student_currency ?? 0);
    const installmentCount = Number(admission?.installment_count ?? 0);
    if (!installmentCount) return;

    const expectedInr = getExpectedInstallmentAmount(
      netInr,
      installmentCount,
      installmentNumber
    );
    const expectedStudent = getExpectedInstallmentAmount(
      netStudent,
      installmentCount,
      installmentNumber
    );

    const nextInr = expectedInr !== null ? String(expectedInr) : "";
    const nextStudent = expectedStudent !== null ? String(expectedStudent) : "";
    const currencyCode = admission?.currency_code || "";

    setFormData((prev) => {
      const updates = {
        _admission_net_fee_inr: admission?.net_fee_amount ?? "",
        _admission_net_fee_student: admission?.net_fee_amount_student_currency ?? "",
        _admission_installment_count: admission?.installment_count ?? "",
      };
      if (!isSameValue(prev?.fee_instalment_inr, nextInr)) {
        updates.fee_instalment_inr = nextInr;
      }
      if (!isSameValue(prev?.fee_instalment_student_student_currency, nextStudent)) {
        updates.fee_instalment_student_student_currency = nextStudent;
      }
      if (currencyCode && !isSameValue(prev?.student_currency_code, currencyCode)) {
        updates.student_currency_code = currencyCode;
      }

      const hasChanges = Object.keys(updates).some(
        (key) => !isSameValue(prev?.[key], updates[key])
      );
      return hasChanges ? { ...prev, ...updates } : prev;
    });
  }, [
    baseUrl,
    formData?.organization_ems_admission_id,
    formData?.installment_number,
    selectOptions?.Admissions,
    setFormData,
  ]);

  useEffect(() => {
    const isAdmissionsForm = baseUrl === "/api/ems/admissions";
    if (!isAdmissionsForm) return;

    const toNum = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const roundMoney = (value) => Math.round(Number(value) * 100) / 100;

    const totalInr = toNum(formData?.total_fee_amount);
    const discountInr = toNum(formData?.discount_amount) ?? 0;
    const totalStudent = toNum(formData?.total_fee_amount_student_currency);

    let nextNetInr = formData?.net_fee_amount;
    if (totalInr !== null) {
      nextNetInr = String(Math.max(roundMoney(totalInr - discountInr), 0));
    }

    let nextDiscountStudent = formData?.discount_amount_student_currency;
    if (totalInr !== null && totalStudent !== null && totalInr > 0) {
      nextDiscountStudent = String(
        Math.max(roundMoney((discountInr / totalInr) * totalStudent), 0)
      );
    } else if (totalStudent !== null && discountInr === 0) {
      nextDiscountStudent = "0";
    }

    const discountStudent = toNum(nextDiscountStudent) ?? 0;

    let nextNetStudent = formData?.net_fee_amount_student_currency;
    if (totalStudent !== null) {
      nextNetStudent = String(
        Math.max(roundMoney(totalStudent - discountStudent), 0)
      );
    }

    const nextDiscStudentNum = Number(nextDiscountStudent);
    const hasPositiveStudentDiscount =
      nextDiscountStudent !== "" &&
      nextDiscountStudent !== null &&
      nextDiscountStudent !== undefined &&
      Number.isFinite(nextDiscStudentNum) &&
      nextDiscStudentNum > 0;

    const shouldClearStudentDiscountReason =
      !hasPositiveStudentDiscount &&
      !!String(formData?.discount_reason_student_currency ?? "").trim();

    const hasNetInrChange = !isSameValue(formData?.net_fee_amount, nextNetInr);
    const hasNetStudentChange = !isSameValue(
      formData?.net_fee_amount_student_currency,
      nextNetStudent
    );
    const hasDiscountStudentChange = !isSameValue(
      formData?.discount_amount_student_currency,
      nextDiscountStudent
    );

    if (
      !hasNetInrChange &&
      !hasNetStudentChange &&
      !hasDiscountStudentChange &&
      !shouldClearStudentDiscountReason
    ) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      ...(hasNetInrChange ? { net_fee_amount: nextNetInr } : null),
      ...(hasDiscountStudentChange
        ? { discount_amount_student_currency: nextDiscountStudent }
        : null),
      ...(hasNetStudentChange
        ? { net_fee_amount_student_currency: nextNetStudent }
        : null),
      ...(shouldClearStudentDiscountReason
        ? { discount_reason_student_currency: "" }
        : null),
    }));
  }, [
    baseUrl,
    formData?.total_fee_amount,
    formData?.discount_amount,
    formData?.total_fee_amount_student_currency,
    formData?.discount_amount_student_currency,
    formData?.net_fee_amount,
    formData?.net_fee_amount_student_currency,
    formData?.discount_reason_student_currency,
    setFormData,
  ]);

  // Leads -> Students transfer fallback:
  // If state/city IDs don't match dropdown options, use state_name/city_name to pick IDs.
  useEffect(() => {
    const isStudentForm = baseUrl === "/api/ems/students";
    if (!isStudentForm) return;

    const normalize = (v) =>
      String(v ?? "")
        .trim()
        .toLowerCase();

    const stateName = formData?.state_name;
    const cityName = formData?.city_name;

    if (stateName && Array.isArray(selectOptions?.States) && selectOptions?.States.length > 0) {
      const stateTarget = normalize(stateName);
      const match = selectOptions.States.find((s) => {
        return (
          normalize(s?.state_name) === stateTarget ||
          normalize(s?.name) === stateTarget ||
          normalize(s?.state) === stateTarget
        );
      });

      if (match) {
        const nextStateId =
          match?.general_state_id ??
          match?.state_general_state_id ??
          match?.organization_general_state_id ??
          match?.state_id ??
          match?.id ??
          "";
        if (nextStateId && !isSameValue(nextStateId, formData?.state_id)) {
          setFormData((prev) => ({
            ...prev,
            state_id: nextStateId,
            // Clear city so FetchDepend can reload Cities for the selected state
            city_id: "",
          }));
        }
      }
    }

    if (cityName && Array.isArray(selectOptions?.Cities) && selectOptions?.Cities.length > 0) {
      const cityTarget = normalize(cityName);
      const match = selectOptions.Cities.find((c) => {
        return (
          normalize(c?.city_name) === cityTarget ||
          normalize(c?.name) === cityTarget ||
          normalize(c?.city) === cityTarget
        );
      });

      if (match) {
        const nextCityId =
          match?.general_city_id ??
          match?.city_general_city_id ??
          match?.organization_general_city_id ??
          match?.city_id ??
          match?.id ??
          "";
        if (nextCityId && !isSameValue(nextCityId, formData?.city_id)) {
          setFormData((prev) => ({ ...prev, city_id: nextCityId }));
        }
      }
    }
  }, [
    baseUrl,
    formData?.state_name,
    formData?.city_name,
    formData?.state_id,
    formData?.city_id,
    selectOptions?.States,
    selectOptions?.Cities,
    setFormData,
  ]);

  const fetchDependentData = async (fetcher) => {
    try {
      const dependKey = fetcher.dependKey;
      const dependValue = formData[dependKey];

      if (!dependValue) {
        setSelectOptions((prev) => ({ ...prev, [fetcher.name]: [] }));
        return;
      }

      const dynamicLink = replaceOrgIdInApiLink(fetcher.link, organizationId)
        .replace("${country_id}", formData.country_id || "")
        .replace("${state_id}", formData.state_id || "")
        .replace("${training_program_category_id}", formData.training_program_category_id || "");

      const params = { per_page: "all", organization_id: organizationId };
      
      // Add selected type for Employees/Trainers
      if ((fetcher.name === "Trainers" || fetcher.name === "Employees") && personTypeId) {
        if (/^\d+$/.test(String(personTypeId))) params.organization_user_type_id = personTypeId;
        else params.user_type = personTypeId;
      }

      const res = await axios.get(`${MAIN_URL}${dynamicLink}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: params,
      });

      console.log("res.data.data is : ", res.data.data);

      let data = normalizeApiArray(res);
      
      // Flatten OrganizationPerson / OrganizationUser payloads for Trainers and Employees.
      if (fetcher.name === "Trainers" || fetcher.name === "Employees") {
        data = data.map((item) => normalizeTrainerEmployeeOption(item));
        
        if (personTypeId) {
          const selectedTypeLower = String(personTypeId).trim().toLowerCase();
          if (/^\d+$/.test(selectedTypeLower)) {
            const personTypeNum = parseInt(selectedTypeLower, 10);
            data = data.filter((item) => {
              const candidateTypeIds = [
                item?.organization_user_type_id,
                item?.organization_user?.organization_user_type_id,
                item?.person_type_id,
                item?.organization_person?.person_type_id,
              ]
                .map((v) => (v == null || v === "" ? null : parseInt(v, 10)))
                .filter((v) => Number.isInteger(v));
              return candidateTypeIds.includes(personTypeNum);
            });
          } else {
            data = data.filter((item) => {
              const itemType = String(
                item?.user_type_label ??
                  item?.user_type_name ??
                  item?.user_type ??
                  item?.application_user?.user_type_name ??
                  item?.application_user?.user_type ??
                  item?.application_user?.application_user_type ??
                  item?.organization_user?.user_type_name ??
                  item?.organization_user?.user_type ??
                  ""
              )
                .trim()
                .toLowerCase();
              return itemType === selectedTypeLower;
            });
          }
        }
      }

      setSelectOptions((prev) => ({
        ...prev,
        [fetcher.name]: data,
      }));
    } catch (error) {
      console.error(`Failed to fetch dependent data for ${fetcher.name}:`, error);
    }
  };

  useEffect(() => {
    if (!DataFetchers?.length) return;

    DataFetchers.forEach((fetcher) => {
      if (fetcher.Type === "FetchDepend") {
        const dependKey = fetcher.dependKey;


        if (formData[dependKey]) {
          fetchDependentData(fetcher);
        } else {

          setSelectOptions((prev) => ({ ...prev, [fetcher.name]: [] }));
        }
      }
    });
  }, [formData.country_id, formData.state_id , formData.training_program_category_id]);

  useEffect(() => {
    const isStudentFeeForm = baseUrl === "/api/ems/students-fees";
    const isFeeInstallmentForm = baseUrl === "/api/ems/fee-installments";
    const isBatchStudentForm = baseUrl === "/api/ems/batch-students";
    const isCertificateForm = baseUrl === "/api/ems/certificates";
    const isAssessmentResultForm = baseUrl === "/api/ems/assesment-results";
    const isStudentReviewForm = baseUrl === "/api/ems/student-reviews";
    const isPlacementReferralForm = baseUrl === "/api/ems/placement-referrals";
    if (
      !isStudentFeeForm &&
      !isFeeInstallmentForm &&
      !isBatchStudentForm &&
      !isCertificateForm &&
      !isAssessmentResultForm &&
      !isStudentReviewForm &&
      !isPlacementReferralForm
    )
      return;

    const selectedStudentId = formData?.organization_ems_student_id;
    const currentAdmissionId = formData?.organization_ems_admission_id;
    const allAdmissions = selectOptions?.Admissions || [];
    const allFeeInstallments = selectOptions?.FeeInstallments || [];
    const currentFeeInstallmentId = formData?.organization_ems_fee_installment_id;

    // If Fee Receipts form is opened with query prefill, don't clear the admission
    // before the Admissions dropdown options are loaded.
    if (isStudentFeeForm && currentAdmissionId && allAdmissions.length === 0) {
      return;
    }

    // Assessment results / student reviews (add/edit with API prefill): same guard so admission is not cleared early.
    if (
      (isAssessmentResultForm || isStudentReviewForm) &&
      currentAdmissionId &&
      allAdmissions.length === 0
    ) {
      return;
    }

    // Batch Students edit/add with prefilled admission: don't clear admission/student
    // before Admissions options are loaded.
    if (isBatchStudentForm && currentAdmissionId && allAdmissions.length === 0) {
      return;
    }

    // Batch Students: when admission is cleared, also clear the auto-filled student.
    if (isBatchStudentForm) {
      const prevAdmissionId = prevBatchStudentAdmissionIdRef.current;
      const nextAdmissionId = currentAdmissionId ?? "";

      if (prevAdmissionId !== undefined) {
        const hadAdmission =
          prevAdmissionId != null && String(prevAdmissionId).trim() !== "";
        const hasAdmission =
          nextAdmissionId != null && String(nextAdmissionId).trim() !== "";

        if (hadAdmission && !hasAdmission && selectedStudentId) {
          prevBatchStudentAdmissionIdRef.current = nextAdmissionId;
          prevBatchAdmissionIdForEnrollmentRef.current = nextAdmissionId;
          setFormData((prev) =>
            isSameValue(prev.organization_ems_student_id, "") &&
            isSameValue(prev.enrollment_date, "")
              ? prev
              : {
                  ...prev,
                  organization_ems_student_id: "",
                  enrollment_date: "",
                }
          );
          return;
        }
      }

      prevBatchStudentAdmissionIdRef.current = nextAdmissionId;
    }

    if ((isFeeInstallmentForm || isBatchStudentForm) && currentAdmissionId) {
      const matchedAdmission = allAdmissions.find((admission) =>
        isSameValue(admission?.organization_ems_admission_id, currentAdmissionId)
      );
      const linkedStudentId = matchedAdmission?.organization_ems_student_id || "";

      if (
        linkedStudentId &&
        !isSameValue(linkedStudentId, selectedStudentId)
      ) {
        setFormData((prev) => ({
          ...prev,
          organization_ems_student_id: linkedStudentId,
        }));
        return;
      }
    }

    if (!selectedStudentId) {
      if (
        currentAdmissionId &&
        (isStudentFeeForm ||
          isCertificateForm ||
          isAssessmentResultForm ||
          isStudentReviewForm ||
          isPlacementReferralForm)
      ) {
        setFormData((prev) => ({
          ...prev,
          organization_ems_admission_id: "",
          ...(isCertificateForm
            ? {
                organization_ems_batch_id: "",
                training_program_id: "",
                _certificate_eligible: true,
                _admission_has_certificate: false,
              }
            : {}),
        }));
      }
      return;
    }

    const studentAdmissions = allAdmissions.filter((admission) =>
      isSameValue(admission?.organization_ems_student_id, selectedStudentId)
    );

    const hasCurrentAdmission = studentAdmissions.some((admission) =>
      isSameValue(admission?.organization_ems_admission_id, currentAdmissionId)
    );

    const nextAdmissionId = (() => {
      if (studentAdmissions.length === 0) return "";
      if (studentAdmissions.length === 1) {
        return studentAdmissions[0]?.organization_ems_admission_id ?? "";
      }
      if (isStudentReviewForm) {
        const sorted = [...studentAdmissions].sort((a, b) => {
          const ta = new Date(a?.admission_date ?? a?.created_at ?? 0).getTime();
          const tb = new Date(b?.admission_date ?? b?.created_at ?? 0).getTime();
          return tb - ta;
        });
        return sorted[0]?.organization_ems_admission_id ?? "";
      }
      return "";
    })();

    // Keep valid selected admission; only auto-adjust when current admission is invalid.
    if (!hasCurrentAdmission) {
      setFormData((prev) =>
        isSameValue(prev.organization_ems_admission_id, nextAdmissionId)
          ? prev
          : { ...prev, organization_ems_admission_id: nextAdmissionId }
      );
    }

    // Placement Referrals: auto-fill training program from the resolved admission.
    if (isPlacementReferralForm) {
      const admissionIdToUse = currentAdmissionId || nextAdmissionId;
      const resolvedAdmission = admissionIdToUse
        ? allAdmissions.find((admission) =>
            isSameValue(
              admission?.organization_ems_admission_id,
              admissionIdToUse
            )
          )
        : null;
      const nextTrainingProgramId =
        resolvedAdmission?.training_program_id ??
        resolvedAdmission?.organization_ems_training_program_id ??
        resolvedAdmission?.training_program?.organization_ems_training_program_id ??
        resolvedAdmission?.training_program?.training_program_id ??
        "";

      if (
        !isSameValue(formData?.training_program_id, nextTrainingProgramId || "")
      ) {
        setFormData((prev) => ({
          ...prev,
          training_program_id: nextTrainingProgramId || "",
        }));
      }
    }

    // Student Receipts (add mode): once admission is resolved, auto-pick the next pending installment.
    if (!isStudentFeeForm) return;

    const admissionIdToUse = currentAdmissionId || nextAdmissionId;
    if (!admissionIdToUse) {
      if (currentFeeInstallmentId) {
        setFormData((prev) => ({
          ...prev,
          organization_ems_fee_installment_id: "",
        }));
      }
      return;
    }

    const matchingInstallments = allFeeInstallments.filter((fi) =>
      isSameValue(
        fi?.organization_ems_admission_id ??
          fi?.admission?.organization_ems_admission_id,
        admissionIdToUse
      )
    );

    const isInstallmentPayable = (fi) => {
      const st = String(fi?.status ?? fi?.installment_status ?? "")
        .trim()
        .toLowerCase();
      // Treat anything except fully closed states as payable so next installment
      // selection keeps working even if API uses variants beyond "pending".
      return !["paid", "cancelled", "refunded", "settled"].includes(st);
    };

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const getDuePriority = (fi) => {
      const ts = new Date(fi?.due_date || 0).getTime();
      if (!Number.isFinite(ts)) return 1;
      return ts <= todayStart.getTime() ? 0 : 1; // due/overdue first
    };

    const pendingInstallments = matchingInstallments
      .filter((fi) => isInstallmentPayable(fi))
      .sort((a, b) => {
        const aDuePriority = getDuePriority(a);
        const bDuePriority = getDuePriority(b);
        if (aDuePriority !== bDuePriority) return aDuePriority - bDuePriority;
        const aNo = Number(a?.installment_number ?? Number.MAX_SAFE_INTEGER);
        const bNo = Number(b?.installment_number ?? Number.MAX_SAFE_INTEGER);
        if (aNo !== bNo) return aNo - bNo;
        const aDue = new Date(a?.due_date || 0).getTime();
        const bDue = new Date(b?.due_date || 0).getTime();
        return aDue - bDue;
      });

    const nextPendingInstallmentId =
      pendingInstallments[0]?.organization_ems_fee_installment_id || "";
    const hasSelectedPendingInstallment =
      nextPendingInstallmentId &&
      isSameValue(currentFeeInstallmentId, nextPendingInstallmentId);

    if (mode === "add") {
      const noPendingInstallment =
        matchingInstallments.length > 0 && !nextPendingInstallmentId;
      setFormErrors((prev) => {
        const currentMsg = prev?.organization_ems_fee_installment_id;
        if (noPendingInstallment) {
          if (currentMsg === FEE_RECEIPT_ALL_PAID_MSG) return prev;
          return {
            ...prev,
            organization_ems_fee_installment_id: FEE_RECEIPT_ALL_PAID_MSG,
          };
        }
        if (currentMsg === FEE_RECEIPT_ALL_PAID_MSG) {
          const { organization_ems_fee_installment_id, ...rest } = prev || {};
          return rest;
        }
        return prev;
      });
    }

    if (mode !== "add" && currentFeeInstallmentId) {
      return;
    }

    if (hasSelectedPendingInstallment) return;

    setFormData((prev) =>
      isSameValue(
        prev.organization_ems_fee_installment_id,
        nextPendingInstallmentId
      )
        ? prev
        : {
            ...prev,
            organization_ems_fee_installment_id: nextPendingInstallmentId,
          }
    );
  }, [
    baseUrl,
    FEE_RECEIPT_ALL_PAID_MSG,
    mode,
    formData?.organization_ems_student_id,
    formData?.organization_ems_admission_id,
    formData?.organization_ems_fee_installment_id,
    selectOptions?.Admissions,
    selectOptions?.FeeInstallments,
    setFormData,
    setFormErrors,
  ]);

  useEffect(() => {
    if (baseUrl !== "/api/ems/students-fees") return;
    const installmentId = formData?.organization_ems_fee_installment_id;
    const allFeeInstallments = Array.isArray(selectOptions?.FeeInstallments)
      ? selectOptions.FeeInstallments
      : [];
    if (!installmentId) {
      setFormData((prev) => {
        const updates = {};
        if (!isSameValue(prev?.amount_received_inr, "")) {
          updates.amount_received_inr = "";
        }
        if (!isSameValue(prev?.student_currency_code, "")) {
          updates.student_currency_code = "";
        }
        if (!isSameValue(prev?.amount_paid_student_currency, "")) {
          updates.amount_paid_student_currency = "";
        }
        return Object.keys(updates).length ? { ...prev, ...updates } : prev;
      });
      return;
    }
    if (allFeeInstallments.length === 0) return;

    const selectedInstallment = allFeeInstallments.find((fi) =>
      isSameValue(fi?.organization_ems_fee_installment_id, installmentId)
    );
    if (!selectedInstallment) return;

    const nextAmountInr =
      selectedInstallment?.fee_instalment_inr ??
      selectedInstallment?.amount_received_inr ??
      "";
    const nextCurrencyCode =
      selectedInstallment?.student_currency_code ??
      selectedInstallment?.currency_code_inr ??
      selectedInstallment?.admission?.currency_code ??
      "";
    const nextAmountStudentCurrency =
      selectedInstallment?.fee_instalment_student_student_currency ??
      selectedInstallment?.amount_paid_student_currency ??
      "";

    setFormData((prev) => {
      const updates = {};
      if (!isSameValue(prev?.amount_received_inr, nextAmountInr)) {
        updates.amount_received_inr = nextAmountInr;
      }
      if (!isSameValue(prev?.student_currency_code, nextCurrencyCode)) {
        updates.student_currency_code = nextCurrencyCode;
      }
      if (
        !isSameValue(
          prev?.amount_paid_student_currency,
          nextAmountStudentCurrency
        )
      ) {
        updates.amount_paid_student_currency = nextAmountStudentCurrency;
      }
      return Object.keys(updates).length ? { ...prev, ...updates } : prev;
    });
  }, [
    baseUrl,
    formData?.organization_ems_student_id,
    formData?.organization_ems_admission_id,
    formData?.organization_ems_fee_installment_id,
    selectOptions?.FeeInstallments,
    setFormData,
  ]);

  const handleMainFormSubmit = () => {
    const isStudentFeeAddMode =
      baseUrl === "/api/ems/students-fees" && mode === "add";
    if (
      isStudentFeeAddMode &&
      formErrors?.organization_ems_fee_installment_id ===
        FEE_RECEIPT_ALL_PAID_MSG
    ) {
      toast.error(FEE_RECEIPT_ALL_PAID_MSG);
      return;
    }
    if (
      baseUrl === "/api/ems/certificates" &&
      formData?._admission_has_certificate
    ) {
      toast.error(
        "A certificate already exists for this admission number."
      );
      return;
    }
    if (embeddedLeadActivityConfig && baseUrl === "/api/ems/leads") {
      const raw = leadActivityEmbedRef.current || {};
      const hasIntent = ["activity_datetime", "lead_direction", "activity_type"].some(
        (k) => raw[k] != null && String(raw[k]).trim() !== ""
      );
      const hasExistingActivity =
        raw?.organization_ems_lead_activity_id != null &&
        String(raw.organization_ems_lead_activity_id).trim() !== "";
      const shouldValidate =
        mode === "add" ||
        hasExistingActivity ||
        (hasIntent && leadActivityEmbedTouched);
      if (shouldValidate) {
        const forValidation = {
          ...raw,
          lead_datetime: formData?.lead_datetime,
          organization_id:
            raw.organization_id ??
            formData?.organization_id ??
            userData?.organization?.organization_id ??
            userData?.organization_id ??
            userData?.user_active_organization?.organization_id ??
            "",
          organization_ems_lead_id:
            formData?.organization_ems_lead_id ||
            formData?.id ||
            "",
        };
        const ok = embeddedLeadActivityConfig.validateForm(
          forValidation,
          setLeadActivityEmbedErrors
        );
        if (!ok) {
          toast.error("Please fix the Lead activity section.");
          return;
        }
      }
    }

    if (baseUrl === "/api/ems/class-records") {
      const batchId = formData?.organization_ems_batch_id;
      const allBatches = selectOptions?.Batches || [];
      const batch = batchId
        ? allBatches.find((b) => isSameValue(b?.organization_ems_batch_id, batchId))
        : null;
      if (batch?.start_date && batch?.end_date) {
        const batchStartYmd = String(batch.start_date).slice(0, 10);
        const batchEndYmd = String(batch.end_date).slice(0, 10);
        const toYmd = (dt) => (dt ? String(dt).slice(0, 10) : null);
        const batchErrors = {};
        const startYmd = toYmd(formData?.start_time);
        if (startYmd) {
          if (startYmd < batchStartYmd)
            batchErrors.start_time = `Start date cannot be before batch start date (${batchStartYmd}).`;
          else if (startYmd > batchEndYmd)
            batchErrors.start_time = `Start date cannot be after batch end date (${batchEndYmd}).`;
        }
        const endYmd = toYmd(formData?.end_time);
        if (endYmd) {
          if (endYmd < batchStartYmd)
            batchErrors.end_time = `End date cannot be before batch start date (${batchStartYmd}).`;
          else if (endYmd > batchEndYmd)
            batchErrors.end_time = `End date cannot be after batch end date (${batchEndYmd}).`;
        }
        if (Object.keys(batchErrors).length > 0) {
          setFormErrors((prev) => ({ ...prev, ...batchErrors }));
          toast.error("Class date must be within the batch start and end dates.");
          return;
        }
      }
    }

    if (baseUrl === "/api/ems/lead-activities" && formData?.organization_ems_lead_id) {
      const leads = selectOptions?.Leads || [];
      const lead = leads.find((l) =>
        isSameValue(l?.organization_ems_lead_id, formData.organization_ems_lead_id)
      );
      const leadDt =
        apiDatetimeToDatetimeLocalEmbed(lead?.lead_datetime) ||
        formData?._lead_datetime ||
        "";
      if (leadDt && leadDt !== formData?._lead_datetime) {
        setFormData((prev) => ({ ...prev, _lead_datetime: leadDt }));
      }
      const activityMsg = validateActivityDatetimeAgainstLead(
        formData?.activity_datetime,
        leadDt
      );
      if (activityMsg) {
        setFormErrors((prev) => ({ ...prev, activity_datetime: activityMsg }));
        toast.error("Please fix the validation errors.");
        return;
      }
    }

    if (hasEmployeesOrTrainers && !personTypeId && !isViewMode) {
      const ptLabel = baseUrl === "/api/ems/lead-activities" ? "Activity By" : "Trainer User Type";
      const ptError = { person_type_id: `${ptLabel} is required.` };
      let mainErrors = {};
      validateForm(
        { ...formData, organization_id: formData.organization_id ?? organizationId ?? "" },
        (e) => { mainErrors = e || {}; }
      );
      setFormErrors({ ...mainErrors, ...ptError });
      toast.error("Please fix the validation errors.");
      return;
    }

    handleSubmit();
  };

  /** Certificates: auto-fill batch + training from admission; gate submit unless admission is completed and all installments are paid. */
  useEffect(() => {
    if (baseUrl !== "/api/ems/certificates") return;

    const studentId = formData?.organization_ems_student_id;
    let admissionId = formData?.organization_ems_admission_id;
    const admissions = selectOptions?.Admissions || [];
    const rawFi = selectOptions?.FeeInstallments;
    const feeInstallments = Array.isArray(rawFi) ? rawFi : [];
    const feeReady = Array.isArray(rawFi);
    const batchStudents = selectOptions?.BatchStudents || [];

    const resolveTrainingProgramId = (adm) =>
      adm?.training_program_id ??
      adm?.organization_ems_training_program_id ??
      adm?.training_program?.organization_ems_training_program_id ??
      "";

    const resolveBatchIdForStudent = (sid, admRecord, admId) => {
      if (!sid) return "";

      const studentRows = batchStudents.filter((row) =>
        isSameValue(row?.organization_ems_student_id, sid)
      );

      const pickBatchFromRow = (row) =>
        row?.organization_ems_batch_id ??
        row?.batch?.organization_ems_batch_id ??
        "";

      if (admId && studentRows.length) {
        const byAdmission = studentRows.find(
          (row) =>
            isSameValue(row?.organization_ems_admission_id, admId) ||
            isSameValue(row?.admission?.organization_ems_admission_id, admId)
        );
        const fromAdmission = pickBatchFromRow(byAdmission);
        if (fromAdmission) return String(fromAdmission);
      }

      const tpId = resolveTrainingProgramId(admRecord);
      if (tpId && studentRows.length) {
        const byProgram = studentRows.find((row) => {
          const batchTp =
            row?.batch?.training_program_id ??
            row?.batch?.trainingProgram?.organization_ems_training_program_id ??
            row?.batch?.training_program?.organization_ems_training_program_id;
          return isSameValue(batchTp, tpId);
        });
        const fromProgram = pickBatchFromRow(byProgram);
        if (fromProgram) return String(fromProgram);
      }

      if (studentRows.length) {
        const sorted = [...studentRows].sort((a, b) => {
          const ta = new Date(a?.enrollment_date ?? a?.created_at ?? 0).getTime();
          const tb = new Date(b?.enrollment_date ?? b?.created_at ?? 0).getTime();
          return tb - ta;
        });
        const preferred =
          sorted.find((row) => {
            const st = String(row?.batch_status ?? "").trim().toLowerCase();
            return (
              st === "" ||
              st === "pursuing" ||
              st === "active" ||
              st === "enrolled"
            );
          }) ?? sorted[0];
        const fromLatest = pickBatchFromRow(preferred);
        if (fromLatest) return String(fromLatest);
      }

      return (
        admRecord?.organization_ems_batch_id ??
        admRecord?.batch?.organization_ems_batch_id ??
        ""
      );
    };

    if (!studentId) {
      setFormData((prev) =>
        prev._certificate_eligible === true &&
        prev._admission_has_certificate === false
          ? prev
          : {
              ...prev,
              _certificate_eligible: true,
              _admission_has_certificate: false,
            }
      );
      return;
    }

    const studentAdmissions = admissions.filter((a) =>
      isSameValue(a?.organization_ems_student_id, studentId)
    );

    if (!admissionId && studentAdmissions.length === 1) {
      admissionId = studentAdmissions[0]?.organization_ems_admission_id ?? "";
    }

    if (!admissionId) {
      setFormData((prev) =>
        prev._certificate_eligible === true &&
        prev._admission_has_certificate === false
          ? prev
          : {
              ...prev,
              _certificate_eligible: true,
              _admission_has_certificate: false,
            }
      );
      return;
    }

    const adm =
      studentAdmissions.find((a) =>
        isSameValue(a.organization_ems_admission_id, admissionId)
      ) ??
      admissions.find((a) =>
        isSameValue(a.organization_ems_admission_id, admissionId)
      );

    const tp = resolveTrainingProgramId(adm);
    const batch = resolveBatchIdForStudent(studentId, adm, admissionId);

    setFormData((prev) => {
      const nextAdmissionId = admissionId || prev.organization_ems_admission_id;
      const nextTp = tp || prev.training_program_id;
      const nextBatch = batch || prev.organization_ems_batch_id;
      if (
        isSameValue(prev.organization_ems_admission_id, nextAdmissionId) &&
        isSameValue(prev.training_program_id, nextTp) &&
        isSameValue(prev.organization_ems_batch_id, nextBatch)
      ) {
        return prev;
      }
      return {
        ...prev,
        organization_ems_admission_id: nextAdmissionId,
        training_program_id: nextTp,
        organization_ems_batch_id: nextBatch,
      };
    });

    if (!feeReady) {
      setFormData((prev) =>
        prev._certificate_eligible !== false
          ? { ...prev, _certificate_eligible: false }
          : prev
      );
      return;
    }

    const isCompleted =
      String(adm?.admission_status ?? "").trim().toLowerCase() === "completed";
    const insts = feeInstallments.filter((fi) =>
      isSameValue(fi.organization_ems_admission_id, admissionId)
    );
    const isPaidStatus = (s) =>
      String(s ?? "")
        .trim()
        .toLowerCase() === "paid";
    const allInstallmentsPaid =
      insts.length === 0 || insts.every((fi) => isPaidStatus(fi?.status));

    const eligible = Boolean(adm) && isCompleted && allInstallmentsPaid;

    const existingCerts = selectOptions?.Certificates || [];
    const currentCertId =
      formData?.organization_ems_certificate_id ?? formData?.id ?? "";
    const admissionHasCertificate = existingCerts.some(
      (c) =>
        isSameValue(c?.organization_ems_admission_id, admissionId) &&
        !(
          mode === "edit" &&
          isSameValue(c?.organization_ems_certificate_id ?? c?.id, currentCertId)
        )
    );

    setFormData((prev) => {
      const nextEligible = eligible && !admissionHasCertificate;
      if (
        prev._certificate_eligible === nextEligible &&
        prev._admission_has_certificate === admissionHasCertificate
      ) {
        return prev;
      }
      return {
        ...prev,
        _certificate_eligible: nextEligible,
        _admission_has_certificate: admissionHasCertificate,
      };
    });
  }, [
    baseUrl,
    mode,
    formData?.organization_ems_student_id,
    formData?.organization_ems_admission_id,
    formData?.organization_ems_certificate_id,
    formData?.id,
    selectOptions?.Admissions,
    selectOptions?.FeeInstallments,
    selectOptions?.BatchStudents,
    selectOptions?.Certificates,
    setFormData,
  ]);

  /** Certificates (add): auto-fill certificate name from selected student's first + last name. */
  useEffect(() => {
    if (baseUrl !== "/api/ems/certificates" || mode !== "add") return;

    const studentId = formData?.organization_ems_student_id;
    const students = selectOptions?.Students || [];
    const selectedStudent = studentId
      ? students.find((s) =>
          isSameValue(s?.organization_ems_student_id, studentId)
        )
      : null;

    const certificateName = selectedStudent
      ? [selectedStudent.first_name, selectedStudent.last_name]
          .filter((s) => s != null && String(s).trim() !== "")
          .map((s) => String(s).trim())
          .join(" ")
      : "";

    setFormData((prev) =>
      prev.certificate_name === certificateName
        ? prev
        : { ...prev, certificate_name: certificateName }
    );
  }, [
    baseUrl,
    mode,
    formData?.organization_ems_student_id,
    selectOptions?.Students,
    setFormData,
  ]);

  // Batch Students: default Enrollment Date from selected Admission's admission_date.
  useEffect(() => {
    if (baseUrl !== "/api/ems/batch-students") return;
    const admissionId = formData?.organization_ems_admission_id;
    const allAdmissions = selectOptions?.Admissions || [];
    if (!allAdmissions.length) return;

    const prev = prevBatchAdmissionIdForEnrollmentRef.current;

    if (prev === undefined) {
      prevBatchAdmissionIdForEnrollmentRef.current = admissionId ?? null;
      if (mode === "add" && admissionId) {
        const matched = allAdmissions.find((a) =>
          isSameValue(a?.organization_ems_admission_id, admissionId)
        );
        const dateStr = matched?.admission_date
          ? String(matched.admission_date).slice(0, 10)
          : "";
        if (dateStr) {
          setFormData((p) =>
            isSameValue(p.enrollment_date, dateStr)
              ? p
              : { ...p, enrollment_date: dateStr }
          );
        }
      }
      return;
    }

    if (isSameValue(prev, admissionId ?? null)) return;

    prevBatchAdmissionIdForEnrollmentRef.current = admissionId ?? null;

    if (!admissionId) return;

    const matched = allAdmissions.find((a) =>
      isSameValue(a?.organization_ems_admission_id, admissionId)
    );
    const dateStr = matched?.admission_date
      ? String(matched.admission_date).slice(0, 10)
      : "";
    if (!dateStr) return;

    setFormData((p) => ({ ...p, enrollment_date: dateStr }));
  }, [
    baseUrl,
    mode,
    formData?.organization_ems_admission_id,
    selectOptions?.Admissions,
    setFormData,
  ]);

  // Batch Students: auto-fill Mode from selected Batch's batch_mode.
  useEffect(() => {
    if (baseUrl !== "/api/ems/batch-students") return;
    const batchId = formData?.organization_ems_batch_id;
    if (!batchId) return;
    const allBatches = selectOptions?.Batches || [];
    if (!allBatches.length) return;
    const matched = allBatches.find((b) =>
      isSameValue(b?.organization_ems_batch_id, batchId)
    );
    const batchMode = matched?.batch_mode ?? matched?.mode ?? "";
    if (!batchMode) return;
    setFormData((p) =>
      isSameValue(p.mode, batchMode) ? p : { ...p, mode: batchMode }
    );
  }, [
    baseUrl,
    formData?.organization_ems_batch_id,
    selectOptions?.Batches,
    setFormData,
  ]);

  // Batch Students: completion date applies only when training status is Completed.
  useEffect(() => {
    if (baseUrl !== "/api/ems/batch-students") return;
    const status = String(formData?.batch_status ?? "").trim().toLowerCase();
    if (status === "completed" || status === "dropped") return;
    if (!formData?.completion_date) return;
    setFormData((prev) =>
      isSameValue(prev.completion_date, "")
        ? prev
        : { ...prev, completion_date: "" }
    );
  }, [
    baseUrl,
    formData?.batch_status,
    formData?.completion_date,
    setFormData,
  ]);

  // Class Records: validate start_time / end_time against selected batch's start_date and end_date in real-time.
  useEffect(() => {
    if (baseUrl !== "/api/ems/class-records") return;
    const batchId = formData?.organization_ems_batch_id;
    const startTime = formData?.start_time;
    const endTime = formData?.end_time;
    if (!batchId) return;

    const allBatches = selectOptions?.Batches || [];
    const batch = allBatches.find((b) =>
      isSameValue(b?.organization_ems_batch_id, batchId)
    );
    if (!batch?.start_date || !batch?.end_date) return;

    const batchStartYmd = String(batch.start_date).slice(0, 10);
    const batchEndYmd = String(batch.end_date).slice(0, 10);
    const toYmd = (dt) => (dt ? String(dt).slice(0, 10) : null);

    const newErrors = {};
    const startYmd = toYmd(startTime);
    if (startYmd) {
      if (startYmd < batchStartYmd) {
        newErrors.start_time = `Start date cannot be before batch start date (${batchStartYmd}).`;
      } else if (startYmd > batchEndYmd) {
        newErrors.start_time = `Start date cannot be after batch end date (${batchEndYmd}).`;
      }
    }
    const endYmd = toYmd(endTime);
    if (endYmd) {
      if (endYmd < batchStartYmd) {
        newErrors.end_time = `End date cannot be before batch start date (${batchStartYmd}).`;
      } else if (endYmd > batchEndYmd) {
        newErrors.end_time = `End date cannot be after batch end date (${batchEndYmd}).`;
      }
    }

    setFormErrors((prev) => {
      const next = { ...prev };
      let changed = false;
      ["start_time", "end_time"].forEach((field) => {
        const msg = newErrors[field] ?? "";
        const prevMsg = prev[field] ?? "";
        // Only touch errors that belong to the batch range check (contain "batch")
        if (msg && prevMsg !== msg) {
          next[field] = msg;
          changed = true;
        } else if (!msg && prevMsg && String(prevMsg).includes("batch")) {
          next[field] = "";
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [
    baseUrl,
    formData?.organization_ems_batch_id,
    formData?.start_time,
    formData?.end_time,
    selectOptions?.Batches,
    setFormErrors,
  ]);

  const handleAccordionFieldChange = (index, name, value) => {
    setParticipantForms((prev) =>
      prev.map((entry, i) =>
        i === index
          ? {
              ...entry,
              formData: { ...entry.formData, [name]: value },
              formErrors: { ...entry.formErrors, [name]: "" },
            }
          : entry
      )
    );
  };

  const addParticipantAccordion = () => {
    const newKey = Date.now() + participantForms.length;
    const prefilledDemoSessionId =
      participantForms[0]?.formData?.organization_ems_demo_session_id ||
      formData?.organization_ems_demo_session_id ||
      new URLSearchParams(location.search).get(
        "organization_ems_demo_session_id"
      ) ||
      "";
    const normalizedDemoSessionId = String(prefilledDemoSessionId).trim();
    setParticipantForms((prev) => [
      ...prev,
      {
        key: newKey,
        formData: {
          ...createParticipantFormData(),
          ...(normalizedDemoSessionId && normalizedDemoSessionId !== "-"
            ? { organization_ems_demo_session_id: normalizedDemoSessionId }
            : {}),
        },
        formErrors: {},
      },
    ]);
    setExpandedParticipantKey(newKey);
  };

  const removeParticipantAccordion = (index) => {
    setParticipantForms((prev) => {
      const removed = prev[index];
      const next = prev.filter((_, i) => i !== index);
      if (!next.length) {
        setExpandedParticipantKey(null);
        return next;
      }
      if (removed?.key === expandedParticipantKey) {
        const fallbackIndex = index > 0 ? index - 1 : 0;
        setExpandedParticipantKey(next[fallbackIndex]?.key ?? next[0].key);
      }
      return next;
    });
  };

  const handleParticipantAccordionsSubmit = async () => {
    let hasAnyError = false;
    const validatedForms = participantForms.map((entry) => {
      let currentErrors = {};
      const valid = validateForm(entry.formData, (errors) => {
        currentErrors = errors || {};
      });
      if (!valid) hasAnyError = true;
      return { ...entry, formErrors: currentErrors };
    });

    if (hasAnyError) {
      setParticipantForms(validatedForms);
      toast.error("Please fix the validation errors.");
      return;
    }

    const organization_entity_id =
      userData?.organization?.entities?.organization_entity_id || null;

    setParticipantBtnLoading(true);
    try {
      for (const entry of validatedForms) {
        const payload = {
          ...entry.formData,
          organization_id: organizationId,
          organization_entity_id,
        };
        await axios.post(`${MAIN_URL}${baseUrl}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      toast.success(successMessages?.add || "Created successfully.");
      navigate(redirectPath);
    } catch (err) {
      if (err.response?.status === 422) {
        toast.error("Please fix the validation errors.");
      } else {
        toast.error("Operation failed");
      }
    } finally {
      setParticipantBtnLoading(false);
    }
  };

  useEffect(() => {
    if (!isDemoSessionParticipantEmbed || mode === "view") {
      setEmbedParticipantForms([]);
      setEmbedExpandedParticipantKey(null);
      return;
    }
    if (mode === "add") {
      setEmbedParticipantForms((prev) => {
        if (prev.length) return prev;
        return [
          {
            key: Date.now(),
            formData: {
              organization_id: organizationId || "",
              organization_entity_id:
                userData?.organization?.entities?.organization_entity_id || "",
              organization_ems_demo_session_id: "",
              organization_ems_lead_id: "",
              attendance_status: "",
              student_remarks: "",
              trainer_remarks: "",
              counsellor_remarks: "",
            },
            formErrors: {},
          },
        ];
      });
      return;
    }
    setEmbedParticipantForms((prev) => {
      const sameSession =
        prev.length > 0 &&
        String(prev[0]?.formData?.organization_ems_demo_session_id) ===
          String(demoSessionEditId);
      if (sameSession) return prev;
      return [
        {
          key: Date.now(),
          formData: {
            organization_id: organizationId || "",
            organization_entity_id:
              userData?.organization?.entities?.organization_entity_id || "",
            organization_ems_demo_session_id: demoSessionEditId,
            organization_ems_lead_id: "",
            attendance_status: "",
            student_remarks: "",
            trainer_remarks: "",
            counsellor_remarks: "",
          },
          formErrors: {},
        },
      ];
    });
  }, [
    isDemoSessionParticipantEmbed,
    mode,
    demoSessionEditId,
    organizationId,
    userData?.organization?.entities?.organization_entity_id,
  ]);

  useEffect(() => {
    if (!isDemoSessionParticipantEmbed || mode === "view") return;
    if (!embedParticipantForms.length) return;
    if (embedExpandedParticipantKey != null) return;
    setEmbedExpandedParticipantKey(embedParticipantForms[0].key);
  }, [
    isDemoSessionParticipantEmbed,
    mode,
    embedParticipantForms,
    embedExpandedParticipantKey,
  ]);

  // Demo session participants: only leads whose training program matches the session.
  useEffect(() => {
    if (baseUrl !== "/api/ems/demo-sessions") return;
    const leads = selectOptions?.Leads;
    if (!Array.isArray(leads) || leads.length === 0) return;

    const tp = formData?.organization_ems_training_program_id;

    setEmbedParticipantForms((prev) => {
      let changed = false;
      const next = prev.map((entry) => {
        const leadId = entry.formData?.organization_ems_lead_id;
        if (!leadId) return entry;

        const lead = leads.find((l) =>
          isSameValue(l?.organization_ems_lead_id, leadId)
        );

        if (!tp) {
          changed = true;
          return {
            ...entry,
            formData: { ...entry.formData, organization_ems_lead_id: "" },
          };
        }

        if (
          !lead ||
          !isSameValue(getLeadTrainingProgramId(lead), tp)
        ) {
          changed = true;
          return {
            ...entry,
            formData: { ...entry.formData, organization_ems_lead_id: "" },
          };
        }
        return entry;
      });
      return changed ? next : prev;
    });
  }, [
    baseUrl,
    formData?.organization_ems_training_program_id,
    selectOptions?.Leads,
  ]);

  // Edit: load embedded rows from API (formData.demo_participants) once per session.
  useEffect(() => {
    if (baseUrl !== "/api/ems/demo-sessions" || mode !== "edit") return;
    const list = formData?.demo_participants;
    if (!Array.isArray(list) || list.length === 0) return;
    if (demoParticipantsHydratedRef.current) return;
    demoParticipantsHydratedRef.current = true;
    setEmbedParticipantForms(
      list.map((row, i) => ({
        key: Date.now() + i,
        formData: {
          organization_id: organizationId || "",
          organization_entity_id:
            userData?.organization?.entities?.organization_entity_id || "",
          organization_ems_demo_session_id: demoSessionEditId,
          organization_ems_lead_id: row.organization_ems_lead_id ?? "",
          attendance_status: row.attendance_status ?? "Scheduled",
          student_remarks: row.student_remarks ?? "",
          trainer_remarks: row.trainer_remarks ?? "",
          counsellor_remarks: row.counsellor_remarks ?? "",
          organization_ems_demo_participant_id:
            row.organization_ems_demo_participant_id ?? "",
        },
        formErrors: {},
      }))
    );
  }, [
    baseUrl,
    mode,
    formData?.demo_participants,
    demoSessionEditId,
    organizationId,
    userData?.organization?.entities?.organization_entity_id,
  ]);

  // Push embedded participant rows into formData so POST/PUT includes demo_participants.
  useEffect(() => {
    if (baseUrl !== "/api/ems/demo-sessions") return;
    if (
      mode === "edit" &&
      !demoParticipantsHydratedRef.current &&
      Array.isArray(formData?.demo_participants) &&
      formData.demo_participants.length > 0
    ) {
      return;
    }
    const next = embedParticipantForms
      .map((e) => e.formData)
      .filter((fd) => fd.organization_ems_lead_id)
      .map((fd) => {
        const row = {
          organization_ems_lead_id: fd.organization_ems_lead_id,
          attendance_status: fd.attendance_status ?? "Scheduled",
          student_remarks: fd.student_remarks ?? "",
          trainer_remarks: fd.trainer_remarks ?? "",
          counsellor_remarks: fd.counsellor_remarks ?? "",
        };
        const pid = fd.organization_ems_demo_participant_id;
        if (pid !== "" && pid !== null && pid !== undefined) {
          row.organization_ems_demo_participant_id = pid;
        }
        return row;
      });
    setFormData((prev) => {
      const cur = prev.demo_participants;
      if (JSON.stringify(cur ?? []) === JSON.stringify(next)) return prev;
      return { ...prev, demo_participants: next };
    });
  }, [
    baseUrl,
    mode,
    formData?.demo_participants,
    embedParticipantForms,
    setFormData,
  ]);

  const handleEmbedParticipantFieldChange = (index, name, value) => {
    setEmbedParticipantForms((prev) =>
      prev.map((entry, i) =>
        i === index
          ? {
              ...entry,
              formData: { ...entry.formData, [name]: value },
              formErrors: { ...entry.formErrors, [name]: "" },
            }
          : entry
      )
    );
  };

  const addEmbedParticipantAccordion = () => {
    const newKey = Date.now() + embedParticipantForms.length;
    setEmbedParticipantForms((prev) => [
      ...prev,
      {
        key: newKey,
        formData: {
          organization_id: organizationId || "",
          organization_entity_id:
            userData?.organization?.entities?.organization_entity_id || "",
          organization_ems_demo_session_id:
            mode === "add" ? "" : demoSessionEditId,
          organization_ems_lead_id: "",
          attendance_status: "",
          student_remarks: "",
          trainer_remarks: "",
          counsellor_remarks: "",
        },
        formErrors: {},
      },
    ]);
    setEmbedExpandedParticipantKey(newKey);
  };

  const removeEmbedParticipantAccordion = (index) => {
    setEmbedParticipantForms((prev) => {
      const removed = prev[index];
      const next = prev.filter((_, i) => i !== index);
      if (!next.length) {
        setEmbedExpandedParticipantKey(null);
        return next;
      }
      if (removed?.key === embedExpandedParticipantKey) {
        const fallbackIndex = index > 0 ? index - 1 : 0;
        setEmbedExpandedParticipantKey(
          next[fallbackIndex]?.key ?? next[0].key
        );
      }
      return next;
    });
  };

  /** Same as main Update — sends session + demo_participants in one PUT payload. */
  const handleEmbedParticipantsSave = () => {
    handleSubmit();
  };

  const inputIndexByMainKey = useMemo(() => {
    const map = {};
    (Inputs || []).forEach((inp, i) => {
      map[inp.mainKey] = i;
    });
    return map;
  }, [Inputs]);

  const laInputIndexByMainKey = useMemo(() => {
    const map = {};
    if (!embeddedLeadActivityConfig?.Inputs) return map;
    embeddedLeadActivityConfig.Inputs.forEach((inp, i) => {
      map[inp.mainKey] = i;
    });
    return map;
  }, [embeddedLeadActivityConfig]);

  const handleLeadActivityEmbedChange = useCallback((name, value) => {
    setLeadActivityEmbedTouched(true);
    setLeadActivityEmbed((prev) => ({ ...(prev || {}), [name]: value }));
    setLeadActivityEmbedErrors((er) => {
      const next = { ...er, [name]: "" };
      const activityDt =
        name === "activity_datetime"
          ? value
          : leadActivityEmbedRef.current?.activity_datetime;
      const msg = validateActivityDatetimeAgainstLead(
        activityDt,
        formData?.lead_datetime
      );
      if (msg) next.activity_datetime = msg;
      return next;
    });
  }, [formData?.lead_datetime]);

  /** CSS Grid templates for field rows (avoids MUI v7 nested Grid layout bugs in Paper). */
  const rowGridTemplateColumns = (row, variant, keyCount) => {
    if (Array.isArray(row?.itemMds) && row.itemMds.length) {
      const md = row.itemMds.map((fr) => `minmax(0,${fr}fr)`).join(" ");
      return { xs: "1fr", md };
    }
    if (variant === "full" || keyCount <= 1) {
      return { xs: "1fr", md: "minmax(0, 1fr)" };
    }
    if (variant === "twoEqual" && keyCount === 2) {
      return { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" };
    }
    if (variant === "fourEqual" && keyCount === 4) {
      return { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" };
    }
    if (variant === "threeEqual") {
      if (keyCount === 3) {
        return { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" };
      }
      if (keyCount === 2) {
        return { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" };
      }
      if (keyCount === 1) {
        return { xs: "1fr", md: "minmax(0, 1fr)" };
      }
    }
    if (variant === "oneThird") {
      return { xs: "1fr", md: "minmax(0, 4fr)" };
    }
    return {
      xs: "1fr",
      md: `repeat(${Math.max(keyCount, 1)}, minmax(0, 1fr))`,
    };
  };

  const renderInputField = (item, idx) => {
    if (item?.hideInAdd && mode === "add") return null;
    if (item?.hideInView && mode === "view") return null;
    if (typeof item?.visibleWhen === "function" && !item.visibleWhen(formData, mode)) {
      return null;
    }

    if (item.type === "LeadActivityEmbed") {
      if (!embeddedLeadActivityConfig) return null;
      if (!leadActivityEmbed) return null;
      const cfg = embeddedLeadActivityConfig;
      const laData = leadActivityEmbed;
      const laErrs = leadActivityEmbedErrors;

      const renderLaOne = (subItem, subKey) => {
        if (!subItem) return null;
        const fieldLabel = subItem.label;
        const showEmpType =
          subItem.OptionMainKey === "Employees" &&
          subItem.mainKey === "organization_user_id";

        if (subItem.type === "DateTime") {
  return (
            <TextField
              key={subKey}
              fullWidth
              type="datetime-local"
              label={fieldLabel}
              name={subItem.mainKey}
              value={laData?.[subItem.mainKey] || ""}
              onChange={(e) =>
                handleLeadActivityEmbedChange(subItem.mainKey, e.target.value)
              }
              error={!!laErrs?.[subItem.mainKey]}
              helperText={
                subItem.mainKey === "activity_datetime"
                  ? buildActivityDatetimeHelperText(
                      laErrs?.[subItem.mainKey],
                      formData?.lead_datetime
                    )
                  : laErrs?.[subItem.mainKey]
              }
              inputProps={{
                min:
                  subItem.mainKey === "activity_datetime" &&
                  formData?.lead_datetime
                    ? formData.lead_datetime
                    : undefined,
              }}
              InputLabelProps={{ shrink: true }}
              disabled={isViewMode}
            />
          );
        }

        if (subItem.type === "MultiRow") {
          return (
            <TextField
              key={subKey}
              fullWidth
              label={fieldLabel}
              name={subItem.mainKey}
              value={laData?.[subItem.mainKey] ?? ""}
              onChange={(e) =>
                handleLeadActivityEmbedChange(subItem.mainKey, e.target.value)
              }
              error={!!laErrs?.[subItem.mainKey]}
              helperText={laErrs?.[subItem.mainKey]}
              multiline
              rows={3}
              disabled={isViewMode}
            />
          );
        }

        if (subItem.type === "Select") {
          const rawOptions = selectOptions?.[subItem.OptionMainKey] || [];
          let effectiveOptions = rawOptions;
          if (
            subItem.mainKey === "organization_ems_lead_stage_id" &&
            subItem.menuKey != null &&
            laData?.organization_ems_lead_stage_id
          ) {
            const selectedId = laData.organization_ems_lead_stage_id;
            const alreadyListed = rawOptions.some((opt) =>
              isSameValue(opt?.[subItem.menuKey], selectedId)
            );
            if (!alreadyListed) {
              const embeddedStage =
                formData?.lead_stage ??
                formData?.leadStage ??
                (Array.isArray(formData?.lead_activities)
                  ? formData.lead_activities.find((a) =>
                      isSameValue(
                        a?.organization_ems_lead_stage_id ??
                          a?.lead_stage?.organization_ems_lead_stage_id,
                        selectedId
                      )
                    )?.lead_stage
                  : null);
              if (embeddedStage) {
                effectiveOptions = [embeddedStage, ...rawOptions];
              }
            }
          }
          const employeeSelect = (
            <Autocomplete
              fullWidth
              disableClearable={subItem.disableClearable === true}
              options={effectiveOptions}
              getOptionLabel={(option) =>
                option ? String(subItem.formatValue(option) ?? "") : ""
              }
              value={
                (() => {
                  const currentVal = laData?.[subItem.mainKey];
                  if (
                    currentVal === null ||
                    currentVal === undefined ||
                    String(currentVal).trim() === ""
                  ) {
                    return null;
                  }
                  return (
                    effectiveOptions.find((opt) => {
                      const optVal = getOptionSelectValue(
                        opt,
                        subItem.menuKey,
                        subItem.OptionMainKey
                      );
                      return isSameValue(optVal, currentVal);
                    }) || null
                  );
                })()
              }
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                if (subItem.menuKey == null) return option === value;
                return isSameValue(
                  option?.[subItem.menuKey],
                  value?.[subItem.menuKey]
                );
              }}
              filterOptions={(options, state) => {
                let inputValue = "";
                if (typeof state === "string") inputValue = state;
                else if (state && typeof state === "object") {
                  inputValue =
                    state.inputValue ?? state.input ?? state.value ?? "";
                }
                const query = String(inputValue).trim().toLowerCase();
                if (!query) return options;
                return options.filter((opt) =>
                  String(subItem.formatValue(opt) ?? "")
                    .toLowerCase()
                    .includes(query)
                );
              }}
              onChange={(event, newValue) => {
                const value =
                  newValue == null
                    ? ""
                    : getOptionSelectValue(
                        newValue,
                        subItem.menuKey,
                        subItem.OptionMainKey
                      );
                handleLeadActivityEmbedChange(subItem.mainKey, value);
              }}
              disabled={
                isViewMode ||
                (showEmpType &&
                  !personTypeId &&
                  !laData?.organization_user_id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={fieldLabel}
                  error={!!laErrs?.[subItem.mainKey]}
                  helperText={laErrs?.[subItem.mainKey]}
                  inputProps={{
                    ...params.inputProps,
                    ...(subItem.selectReadOnly ? { readOnly: true } : {}),
                  }}
                />
              )}
            />
          );
          if (showEmpType) {
            return (
              <Box
                key={subKey}
                sx={{
                  display: "grid",
                  width: "100%",
                  columnGap: 2,
                  rowGap: 2,
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                }}
              >
                <TextField
                  select
                  fullWidth
                  label="Activity By *"
                  value={personTypeId}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setPersonTypeId(nextType);
                    handleLeadActivityEmbedChange("person_type_id", nextType);
                    handleLeadActivityEmbedChange("organization_user_id", "");
                    // Immediately call organization users API for selected type.
                    fetchEmployeesOrTrainers(nextType);
                  }}
                  disabled={isViewMode}
                >
                  {leadActivityUserTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                {employeeSelect}
              </Box>
            );
          }
          return (
            <React.Fragment key={subKey}>{employeeSelect}</React.Fragment>
          );
        }
        return null;
      };

      return (
        <Box key={idx} sx={{ width: "100%", mt: 1 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Lead activity</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {mode === "add"
                  ? "If you fill this section, a lead activity is created after the lead is saved."
                  : "Creates a new activity for this lead when you update the lead."}
              </Typography> */}
              {(() => {
                const statusInlineKeys = [
                  "lead_direction",
                  "activity_type",
                  "call_status",
                ];
                const topKeys = ["activity_datetime"];
                const activityByKeys = ["organization_user_id"];
                const managedKeys = new Set([
                  ...topKeys,
                  ...statusInlineKeys,
                  ...activityByKeys,
                ]);

                return (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        width: "100%",
                        columnGap: 2,
                        rowGap: 2,
                        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr)" },
                        mb: 2,
                      }}
                    >
                      {topKeys.map((mk) => {
                        const siIdx = laInputIndexByMainKey[mk];
                        const subItem =
                          siIdx === undefined ? null : cfg.Inputs[siIdx];
                        return (
                          <Box key={`top-row-${mk}`} sx={{ minWidth: 0, width: "100%" }}>
                            {renderLaOne(subItem, `top-row-${mk}`)}
                          </Box>
                        );
                      })}
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        width: "100%",
                        columnGap: 2,
                        rowGap: 2,
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "repeat(3, minmax(0, 1fr))",
                        },
                        mb: 2,
                      }}
                    >
                      {statusInlineKeys.map((mk) => {
                        const siIdx = laInputIndexByMainKey[mk];
                        const subItem =
                          siIdx === undefined ? null : cfg.Inputs[siIdx];
                        return (
                          <Box key={`status-row-${mk}`} sx={{ minWidth: 0, width: "100%" }}>
                            {renderLaOne(subItem, `status-row-${mk}`)}
                          </Box>
                        );
                      })}
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        width: "100%",
                        columnGap: 2,
                        rowGap: 2,
                        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr)" },
                        mb: 2,
                      }}
                    >
                      {(() => {
                        const siIdx = laInputIndexByMainKey.organization_user_id;
                        const subItem =
                          siIdx === undefined ? null : cfg.Inputs[siIdx];
                        return renderLaOne(subItem, "activity-by-row");
                      })()}
                    </Box>

                    {cfg.fieldGridLayout?.rows?.map((row, rowIdx) => {
                      const filteredMainKeys = (row.mainKeys || []).filter(
                        (mk) => !managedKeys.has(mk)
                      );
                      if (!filteredMainKeys.length) return null;
                      const variant = row.variant || "full";
                      const keyCount = filteredMainKeys.length || 0;
                      const gridCols = rowGridTemplateColumns(row, variant, keyCount);
                      return (
                        <Box
                          key={`la-row-${rowIdx}`}
                          sx={{
                            display: "grid",
                            width: "100%",
                            columnGap: 2,
                            rowGap: 2,
                            gridTemplateColumns: gridCols,
                            mb: 2,
                          }}
                        >
                          {filteredMainKeys.map((mk) => {
                            const siIdx = laInputIndexByMainKey[mk];
                            const subItem =
                              siIdx === undefined ? null : cfg.Inputs[siIdx];
                            return (
                              <Box key={mk} sx={{ minWidth: 0, width: "100%" }}>
                                {renderLaOne(subItem, `${rowIdx}-${mk}`)}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </>
                );
              })()}
            </AccordionDetails>
          </Accordion>
        </Box>
      );
    }

    if (item.type === "DemoParticipantsEmbed") {
      if (mode === "view") return null;
      if (!embeddedParticipantInputs?.length) return null;
      return (
        <Box key={idx} sx={{ width: "100%" }}>
          {mode === "add" && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose the session Training Program above first. Leads listed for each
              participant match that program. Rows with a Lead selected are saved when
              you click Submit; empty rows are ignored.
            </Typography>
          )}
          {embedParticipantForms.map((entry, formIndex) => (
            <Accordion
              key={entry.key}
              expanded={embedExpandedParticipantKey === entry.key}
              onChange={(event, isExpanded) =>
                setEmbedExpandedParticipantKey(isExpanded ? entry.key : null)
              }
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pr: 1,
                  }}
                >
                  <Typography fontWeight={600}>
                    Participant {formIndex + 1}
                  </Typography>
                  {embedParticipantForms.length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeEmbedParticipantAccordion(formIndex);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {embeddedParticipantInputs.map((pItem, pIdx) => {
                    const rawOptions =
                      selectOptions?.[pItem.OptionMainKey] || [];
                    const demoSessionTpId =
                      formData?.organization_ems_training_program_id;
                    const filterEmbedLeadsBySessionProgram =
                      baseUrl === "/api/ems/demo-sessions" &&
                      pItem.mainKey === "organization_ems_lead_id" &&
                      pItem.OptionMainKey === "Leads";
                    const embedSelectOptions = filterEmbedLeadsBySessionProgram
                      ? !demoSessionTpId
                        ? []
                        : rawOptions.filter((lead) =>
                            isSameValue(
                              getLeadTrainingProgramId(lead),
                              demoSessionTpId
                            )
                          )
                      : rawOptions;
                    const fieldLabel =
                      typeof pItem.getLabel === "function"
                        ? pItem.getLabel(entry.formData)
                        : pItem.label;
                    return (
                      <React.Fragment key={`${entry.key}-emb-${pIdx}`}>
                        {pItem.type === "Select" && (
                          <Autocomplete
                            fullWidth
                            options={embedSelectOptions}
                            getOptionLabel={(option) =>
                              option
                                ? String(pItem.formatValue(option) ?? "")
                                : ""
                            }
                            value={
                              embedSelectOptions.find((opt) => {
                                const optVal =
                                  pItem.menuKey == null
                                    ? opt
                                    : opt?.[pItem.menuKey];
                                return isSameValue(
                                  optVal,
                                  entry.formData?.[pItem.mainKey]
                                );
                              }) || null
                            }
                            isOptionEqualToValue={(option, value) => {
                              if (!option || !value) return false;
                              if (pItem.menuKey == null) return option === value;
                              return isSameValue(
                                option?.[pItem.menuKey],
                                value?.[pItem.menuKey]
                              );
                            }}
                            filterOptions={(options, state) => {
                              let inputValue = "";
                              if (typeof state === "string") inputValue = state;
                              else if (state && typeof state === "object") {
                                inputValue =
                                  state.inputValue ??
                                  state.input ??
                                  state.value ??
                                  "";
                              }
                              const query = String(inputValue).trim().toLowerCase();
                              if (!query) return options;
                              return options.filter((opt) =>
                                String(pItem.formatValue(opt) ?? "")
                                  .toLowerCase()
                                  .includes(query)
                              );
                            }}
                            onChange={(event, newValue) => {
                              const value =
                                newValue == null
                                  ? ""
                                  : pItem.menuKey == null
                                    ? newValue
                                    : newValue?.[pItem.menuKey];
                              handleEmbedParticipantFieldChange(
                                formIndex,
                                pItem.mainKey,
                                value
                              );
                            }}
                            disabled={filterEmbedLeadsBySessionProgram && !demoSessionTpId}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={fieldLabel}
                                error={!!entry.formErrors?.[pItem.mainKey]}
                                helperText={
                                  entry.formErrors?.[pItem.mainKey] ||
                                  (filterEmbedLeadsBySessionProgram &&
                                  !demoSessionTpId
                                    ? "Select Training Program in the session form to list leads for that program."
                                    : "")
                                }
                              />
                            )}
                          />
                        )}
                        {pItem.type === "MultiRow" && (
                          <TextField
                            fullWidth
                            label={fieldLabel}
                            name={pItem.mainKey}
                            value={entry.formData?.[pItem?.mainKey]}
                            onChange={(e) =>
                              handleEmbedParticipantFieldChange(
                                formIndex,
                                pItem.mainKey,
                                e.target.value
                              )
                            }
                            error={!!entry.formErrors?.[pItem.mainKey]}
                            helperText={entry.formErrors?.[pItem.mainKey]}
                            multiline
                            rows={3}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addEmbedParticipantAccordion}
            sx={{ mt: 1, mr: 1 }}
          >
            Add participant
          </Button>
          {mode === "edit" && (
            <Button
              variant="contained"
              color="primary"
              disabled={btnLoading}
              onClick={handleEmbedParticipantsSave}
            sx={{ mt: 1 }}
          >
              Save participants
          </Button>
          )}
        </Box>
      );
    }

          // Add person type dropdown before Employees or Trainers fields
    const isEmployeeOrTrainerField =
      item.OptionMainKey === "Employees" || item.OptionMainKey === "Trainers";
    const showPersonTypeBefore =
      hasEmployeesOrTrainers &&
      isEmployeeOrTrainerField &&
      !Inputs.slice(0, idx).some((input) => input.mainKey === "person_type_id");
          const rawOptions = selectOptions?.[item.OptionMainKey] || [];
          const isStudentFeeForm = baseUrl === "/api/ems/students-fees";
          const isFeeInstallmentForm = baseUrl === "/api/ems/fee-installments";
          const isBatchStudentForm = baseUrl === "/api/ems/batch-students";
          const isItemDisabled =
            typeof item?.disabledWhen === "function"
              ? !!item.disabledWhen(formData, mode)
              : false;
          const isFeeReceiptFromInstallmentPrefill =
            isStudentFeeForm &&
            mode === "add" &&
            new URLSearchParams(location.search).get("from_fee_installment") ===
              "1";
          const FEE_RECEIPT_INSTALLMENT_LOCK_KEYS = new Set([
            "organization_ems_student_id",
            "organization_ems_admission_id",
            "organization_ems_fee_installment_id",
            "student_currency_code",
            "amount_paid_student_currency",
            "amount_received_inr",
          ]);
          const isLockedFeeReceiptInstallment =
            isFeeReceiptFromInstallmentPrefill &&
            FEE_RECEIPT_INSTALLMENT_LOCK_KEYS.has(item.mainKey);
          const isStudentForm = baseUrl === "/api/ems/students";
          const isStudentFromLeadPrefill =
            isStudentForm &&
            mode === "add" &&
            new URLSearchParams(location.search).get("from_lead") === "1";
          const STUDENT_FROM_LEAD_LOCK_KEYS = new Set([
            "organization_ems_lead_id",
            "first_name",
            "last_name",
            "country_id",
            "state_id",
            "city_id",
          ]);
          const isLockedByStudentLeadPrefill =
            isStudentFromLeadPrefill &&
            STUDENT_FROM_LEAD_LOCK_KEYS.has(item.mainKey);
          const isAdmissionForm = baseUrl === "/api/ems/admissions";
          const isAdmissionFromStudentPrefill =
            isAdmissionForm &&
            mode === "add" &&
            new URLSearchParams(location.search).get("from_student") === "1";
          const isLockedAdmissionStudentPrefill =
            isAdmissionFromStudentPrefill &&
            item.mainKey === "organization_ems_student_id";
          const isLeadActivityForm = baseUrl === "/api/ems/lead-activities";
          const isLeadActivityFromLeadsListPrefill =
            isLeadActivityForm &&
            mode === "add" &&
            new URLSearchParams(location.search).get("from_leads_list") === "1";
          const isLockedLeadActivityLeadPrefill =
            isLeadActivityFromLeadsListPrefill &&
            item.mainKey === "organization_ems_lead_id";
          const isLockedByContextPrefill =
            isLockedFeeReceiptInstallment ||
            isLockedByStudentLeadPrefill ||
            isLockedAdmissionStudentPrefill ||
            isLockedLeadActivityLeadPrefill;
          const isAutoLockedStudentFeeInstallment = false;
          const isCertificateForm = baseUrl === "/api/ems/certificates";
          const isAssessmentResultForm =
            baseUrl === "/api/ems/assesment-results";
          const isStudentReviewForm = baseUrl === "/api/ems/student-reviews";
          const isLinkedStudentAdmissionForm =
            isStudentFeeForm ||
            isFeeInstallmentForm ||
            isBatchStudentForm ||
            isCertificateForm ||
            isAssessmentResultForm ||
            isStudentReviewForm;
          const isAdmissionField =
            item.mainKey === "organization_ems_admission_id" &&
            item.OptionMainKey === "Admissions";
          const isStudentField =
            item.mainKey === "organization_ems_student_id" &&
            item.OptionMainKey === "Students";
          const selectedAdmission = (selectOptions?.Admissions || []).find((admission) =>
            isSameValue(
              admission?.organization_ems_admission_id,
              formData?.organization_ems_admission_id
            )
          );
          const shouldFilterAdmissionsByStudent =
            isAdmissionField &&
            formData?.organization_ems_student_id &&
            isLinkedStudentAdmissionForm;
          const fieldOptions = shouldFilterAdmissionsByStudent
            ? rawOptions.filter((admission) =>
                isSameValue(
                  admission?.organization_ems_student_id,
                  formData?.organization_ems_student_id
                )
              )
            : (isFeeInstallmentForm || isBatchStudentForm) &&
                isStudentField &&
                selectedAdmission
              ? rawOptions.filter((student) =>
                  isSameValue(
                    student?.organization_ems_student_id,
                    selectedAdmission?.organization_ems_student_id
                  )
                )
            : rawOptions;

          let resolvedFieldOptions = fieldOptions;
          // Future batches may only be Scheduled or Cancelled
          if (
            baseUrl === "/api/ems/batches" &&
            item.mainKey === "status" &&
            item.OptionMainKey === "Statuses" &&
            formData?.start_date &&
            formData?.end_date
          ) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const start = new Date(formData.start_date);
            start.setHours(0, 0, 0, 0);
            const end = new Date(formData.end_date);
            end.setHours(0, 0, 0, 0);
            if (
              !Number.isNaN(start.getTime()) &&
              !Number.isNaN(end.getTime()) &&
              start > today &&
              end > today
            ) {
              resolvedFieldOptions = fieldOptions.filter((opt) => {
                const value =
                  typeof opt === "string" ? opt : String(opt?.value ?? opt ?? "");
                return value === "Scheduled" || value === "Cancelled";
              });
            }
          }
          if (
            isStudentForm &&
            item.OptionMainKey === "Leads" &&
            item.mainKey === "organization_ems_lead_id"
          ) {
            resolvedFieldOptions = filterLeadsForStudentEnrollment(
              fieldOptions,
              { keepLeadId: formData?.organization_ems_lead_id }
            );
          }
          if (
            isFeeInstallmentForm &&
            mode === "add" &&
            item.OptionMainKey === "Admissions"
          ) {
            const allInstallments = Array.isArray(selectOptions?.FeeInstallments)
              ? selectOptions.FeeInstallments
              : [];
            const currentAdmissionId = formData?.organization_ems_admission_id;
            resolvedFieldOptions = fieldOptions.filter(
              (admission) =>
                canCreateMoreFeeInstallments(admission, allInstallments) ||
                isSameValue(
                  admission?.organization_ems_admission_id,
                  currentAdmissionId
                )
            );
          }
          if (
            isFeeInstallmentForm &&
            mode === "add" &&
            isStudentField
          ) {
            const allInstallments = Array.isArray(selectOptions?.FeeInstallments)
              ? selectOptions.FeeInstallments
              : [];
            const eligibleStudentIds = new Set(
              (selectOptions?.Admissions || [])
                .filter((admission) =>
                  canCreateMoreFeeInstallments(admission, allInstallments)
                )
                .map((admission) =>
                  String(admission?.organization_ems_student_id ?? "")
                )
                .filter(Boolean)
            );
            const currentStudentId = formData?.organization_ems_student_id;
            resolvedFieldOptions = fieldOptions.filter(
              (student) =>
                eligibleStudentIds.has(
                  String(student?.organization_ems_student_id ?? "")
                ) ||
                isSameValue(
                  student?.organization_ems_student_id,
                  currentStudentId
                )
            );
          }
          if (isAssessmentResultForm && isStudentField) {
            const selectedAssessment = (selectOptions?.Assesments || []).find(
              (assessment) =>
                isSameValue(
                  assessment?.organization_ems_assessment_id,
                  formData?.organization_ems_assessment_id
                )
            );
            const selectedBatchId =
              selectedAssessment?.organization_ems_batch_id ??
              selectedAssessment?.batch?.organization_ems_batch_id;
            if (selectedBatchId) {
              const allowedStudentIds = new Set(
                (Array.isArray(selectOptions?.BatchStudents)
                  ? selectOptions.BatchStudents
                  : []
                )
                  .filter((row) =>
                    isSameValue(
                      row?.organization_ems_batch_id ??
                        row?.batch?.organization_ems_batch_id,
                      selectedBatchId
                    )
                  )
                  .map(
                    (row) =>
                      row?.organization_ems_student_id ??
                      row?.student?.organization_ems_student_id
                  )
                  .filter((sid) => sid != null && String(sid).trim() !== "")
                  .map((sid) => String(sid))
              );
              const currentStudentId = formData?.organization_ems_student_id;
              resolvedFieldOptions = fieldOptions.filter(
                (student) =>
                  allowedStudentIds.has(
                    String(student?.organization_ems_student_id ?? "")
                  ) ||
                  isSameValue(
                    student?.organization_ems_student_id,
                    currentStudentId
                  )
              );
            }
          }
          if (isStudentFeeForm && item.OptionMainKey === "FeeInstallments") {
            const admissionId = formData?.organization_ems_admission_id;
            const studentId = formData?.organization_ems_student_id;
            if (admissionId) {
              resolvedFieldOptions = rawOptions.filter((fi) =>
                isSameValue(
                  fi?.organization_ems_admission_id ??
                    fi?.admission?.organization_ems_admission_id,
                  admissionId
                )
              );
            } else if (studentId) {
              resolvedFieldOptions = rawOptions.filter((fi) =>
                isSameValue(
                  fi?.organization_ems_student_id ??
                    fi?.student?.organization_ems_student_id,
                  studentId
                )
              );
            } else {
              resolvedFieldOptions = [];
            }

            if (mode === "add") {
              const isPayable = (fi) => {
                const st = String(fi?.status ?? fi?.installment_status ?? "")
                  .trim()
                  .toLowerCase();
                return !["paid", "cancelled", "refunded", "settled"].includes(st);
              };
              const todayStart = new Date();
              todayStart.setHours(0, 0, 0, 0);
              const getDuePriority = (fi) => {
                const ts = new Date(fi?.due_date || 0).getTime();
                if (!Number.isFinite(ts)) return 1;
                return ts <= todayStart.getTime() ? 0 : 1; // due/overdue first
              };
              resolvedFieldOptions = resolvedFieldOptions
                .filter((fi) => isPayable(fi))
                .sort((a, b) => {
                  const aDuePriority = getDuePriority(a);
                  const bDuePriority = getDuePriority(b);
                  if (aDuePriority !== bDuePriority) {
                    return aDuePriority - bDuePriority;
                  }
                  const aNo = Number(a?.installment_number ?? Number.MAX_SAFE_INTEGER);
                  const bNo = Number(b?.installment_number ?? Number.MAX_SAFE_INTEGER);
                  if (aNo !== bNo) return aNo - bNo;
                  const aDue = new Date(a?.due_date || 0).getTime();
                  const bDue = new Date(b?.due_date || 0).getTime();
                  return aDue - bDue;
                });
            }
          }

          if (
            isCertificateForm &&
            isAdmissionField &&
            Array.isArray(selectOptions?.Certificates)
          ) {
            const existingCerts = selectOptions.Certificates;
            const currentCertId =
              formData?.organization_ems_certificate_id ?? formData?.id ?? "";
            resolvedFieldOptions = resolvedFieldOptions.filter((adm) => {
              const taken = existingCerts.some(
                (c) =>
                  isSameValue(
                    c?.organization_ems_admission_id,
                    adm?.organization_ems_admission_id
                  ) &&
                  !(
                    mode === "edit" &&
                    isSameValue(
                      c?.organization_ems_certificate_id ?? c?.id,
                      currentCertId
                    )
                  )
              );
              return !taken;
            });
          }

          if (
            baseUrl === "/api/ems/class-records" &&
            item.OptionMainKey === "BatchClasses"
          ) {
            const bid = formData?.organization_ems_batch_id;
            resolvedFieldOptions = bid
              ? rawOptions.filter((bc) =>
                  isSameValue(
                    bc?.organization_ems_batch_id ??
                      bc?.batch?.organization_ems_batch_id,
                    bid
                  )
                )
              : [];
          }

          if (
            baseUrl === "/api/ems/classes-attendance" &&
            item.OptionMainKey === "ClassRecords"
          ) {
            const batchClassId = formData?.organization_ems_batch_class_id;
            resolvedFieldOptions = batchClassId
              ? rawOptions.filter((cr) =>
                  isSameValue(
                    cr?.organization_ems_batch_class_id ??
                      cr?.batch_class?.organization_ems_batch_class_id ??
                      cr?.batchClass?.organization_ems_batch_class_id,
                    batchClassId
                  )
                )
              : [];
          }

          if (
            baseUrl === "/api/ems/classes-attendance" &&
            item.OptionMainKey === "Students"
          ) {
            const batchClassId = formData?.organization_ems_batch_class_id;
            if (batchClassId) {
              const batchClasses = Array.isArray(selectOptions?.BatchClasses)
                ? selectOptions.BatchClasses
                : [];
              const selectedBatchClass = batchClasses.find((bc) =>
                isSameValue(
                  bc?.organization_ems_batch_class_id ??
                    bc?.batch_class?.organization_ems_batch_class_id,
                  batchClassId
                )
              );
              const allowedStudentIds = new Set(
                (Array.isArray(selectedBatchClass?.batch_students)
                  ? selectedBatchClass.batch_students
                  : []
                )
                  .map(
                    (bs) =>
                      bs?.organization_ems_student_id ??
                      bs?.student?.organization_ems_student_id
                  )
                  .filter(
                    (sid) =>
                      sid !== undefined &&
                      sid !== null &&
                      String(sid).trim() !== ""
                  )
                  .map((sid) => String(sid))
              );
              resolvedFieldOptions = rawOptions.filter((st) =>
                allowedStudentIds.has(String(st?.organization_ems_student_id))
              );
            } else {
              resolvedFieldOptions = [];
            }
          }

          // Hide inactive training spaces in dropdowns (Batch Classes/Class Records/etc.).
          if (item.OptionMainKey === "TrainingSpaces") {
            resolvedFieldOptions = (Array.isArray(resolvedFieldOptions)
              ? resolvedFieldOptions
              : []
            ).filter((space) => {
              const st = String(space?.status ?? "").trim().toLowerCase();
              return st !== "inactive";
            });
          }

          // Batch Students: only Scheduled / Ongoing batches are open for enrollment.
          if (
            baseUrl === "/api/ems/batch-students" &&
            item.OptionMainKey === "Batches"
          ) {
            const openBatchStatuses = new Set(["scheduled", "ongoing"]);
            resolvedFieldOptions = (Array.isArray(resolvedFieldOptions)
              ? resolvedFieldOptions
              : []
            ).filter((batch) => {
              const status = String(batch?.status ?? "").trim().toLowerCase();
              if (
                (mode === "edit" || mode === "view") &&
                isSameValue(
                  batch?.organization_ems_batch_id,
                  formData?.organization_ems_batch_id
                )
              ) {
                return true;
              }
              return openBatchStatuses.has(status);
            });
          }

          // Assessments: show only batches related to selected training program.
          if (
            baseUrl === "/api/ems/assesments" &&
            item.OptionMainKey === "Batches"
          ) {
            const selectedProgramId = formData?.training_program_id;
            const selectedBatchId = formData?.organization_ems_batch_id;
            const allBatchOptions = Array.isArray(resolvedFieldOptions)
              ? resolvedFieldOptions
              : [];

            resolvedFieldOptions = selectedProgramId
              ? allBatchOptions.filter((batch) => {
                  if (
                    (mode === "edit" || mode === "view") &&
                    isSameValue(
                      batch?.organization_ems_batch_id,
                      selectedBatchId
                    )
                  ) {
                    return true;
                  }
                  const batchProgramId =
                    batch?.training_program_id ??
                    batch?.training_program?.organization_ems_training_program_id ??
                    batch?.training_program?.training_program_id;
                  return isSameValue(batchProgramId, selectedProgramId);
                })
              : [];
          }

          // Batch Classes / Class Records: only Ongoing batches in batch dropdown.
          if (
            (baseUrl === "/api/ems/batch-classes" ||
              baseUrl === "/api/ems/class-records") &&
            item.OptionMainKey === "Batches"
          ) {
            resolvedFieldOptions = filterOngoingBatchesForForm(
              resolvedFieldOptions,
              formData,
              mode
            );
          }

          // Class Attendance: only batch classes whose batch is Ongoing.
          if (
            baseUrl === "/api/ems/classes-attendance" &&
            item.OptionMainKey === "BatchClasses"
          ) {
            const allBatches = Array.isArray(selectOptions?.Batches)
              ? selectOptions.Batches
              : [];
            resolvedFieldOptions = (Array.isArray(resolvedFieldOptions)
              ? resolvedFieldOptions
              : []
            ).filter((batchClass) => {
              if (
                (mode === "edit" || mode === "view") &&
                isSameValue(
                  batchClass?.organization_ems_batch_class_id,
                  formData?.organization_ems_batch_class_id
                )
              ) {
                return true;
              }
              return getBatchClassRunStatus(batchClass, allBatches) === "ongoing";
            });
          }

          if (
            baseUrl === "/api/ems/class-records" &&
            item.OptionMainKey === "BatchClasses" &&
            (mode === "edit" || mode === "view") &&
            formData?.organization_ems_batch_class_id &&
            item.menuKey != null
          ) {
            const selectedId = formData.organization_ems_batch_class_id;
            const embedded = formData.batchClass ?? formData.batch_class;
            if (embedded && isSameValue(embedded[item.menuKey], selectedId)) {
              const alreadyListed = resolvedFieldOptions.some((opt) =>
                isSameValue(opt?.[item.menuKey], selectedId)
              );
              if (!alreadyListed) {
                resolvedFieldOptions = [embedded, ...resolvedFieldOptions];
              }
            }
          }

          let effectiveOptions = resolvedFieldOptions;
          if (
            item.type === "Select" &&
            isEmployeeOrTrainerField &&
            (mode === "edit" || mode === "view") &&
            formData?.[item.mainKey] &&
            item.menuKey != null
          ) {
            const selectedId = formData[item.mainKey];
            const embedded =
              item.OptionMainKey === "Employees"
                ? formData.trainer ?? formData.employee
                : formData.trainer;
      if (embedded && isSameValue(embedded[item.menuKey], selectedId)) {
              const alreadyListed = resolvedFieldOptions.some((opt) =>
                isSameValue(opt?.[item.menuKey], selectedId)
              );
              if (!alreadyListed) {
                effectiveOptions = [embedded, ...resolvedFieldOptions];
              }
            }
          }

          if (
            item.type === "Select" &&
            isStudentFeeForm &&
            item.OptionMainKey === "FeeInstallments" &&
            (mode === "edit" || mode === "view") &&
            formData?.organization_ems_fee_installment_id &&
            item.menuKey != null
          ) {
            const selectedId = formData[item.mainKey];
            const embedded =
              formData.fee_installment ??
              formData.installment ??
              formData.feeInstallment;
            if (
              embedded &&
              isSameValue(embedded[item.menuKey], selectedId)
            ) {
              const alreadyListed = effectiveOptions.some((opt) =>
                isSameValue(opt?.[item.menuKey], selectedId)
              );
              if (!alreadyListed) {
                effectiveOptions = [embedded, ...effectiveOptions];
              }
            }
          }

    const fieldLabel =
      typeof item.getLabel === "function"
        ? item.getLabel(formData)
        : item.label;
    const showInlinePersonTypeAndTrainer =
      showPersonTypeBefore &&
      (baseUrl === "/api/ems/lead-activities" ||
        baseUrl === "/api/ems/batch-classes") &&
      item.mainKey === "organization_user_id";

          return (
            <React.Fragment key={idx}>
              {showPersonTypeBefore && (
                <TextField
                  select
                  fullWidth
                  label={
                    baseUrl === "/api/ems/lead-activities"
                      ? "Activity By *"
                      : "Trainer User Type *"
                  }
                  name="person_type_id"
                  value={personTypeId}
                  error={!!formErrors?.person_type_id}
                  helperText={formErrors?.person_type_id || ""}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setPersonTypeId(nextType);
                    if (formErrors?.person_type_id)
                      setFormErrors((prev) => { const n = { ...prev }; delete n.person_type_id; return n; });
                    // Clear the employee/trainer field when person type changes
                    if (item.OptionMainKey === "Employees") {
                      setFormData((prev) => ({ ...prev, organization_user_id: "" }));
                    } else if (item.OptionMainKey === "Trainers") {
                      setFormData((prev) => ({ ...prev, trainer_employee_id: "" }));
                    }
                    // Immediately call organization users API for selected type.
                    fetchEmployeesOrTrainers(nextType);
                  }}
                  sx={
                    showInlinePersonTypeAndTrainer
                      ? {
                          mb: 2,
                          width: { xs: "100%", md: "calc(50% - 8px)" },
                          display: "inline-flex",
                          mr: { xs: 0, md: 2 },
                          verticalAlign: "top",
                        }
                      : { mb: 2 }
                  }
                  disabled={isViewMode}
                >
                  {(baseUrl === "/api/ems/lead-activities"
                    ? leadActivityUserTypeOptions
                    : userTypeOptions
                  ).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {item.type == "Select" && (
                <>
                  {mode == "add" && item.mainKey == "lead_stage_id" && baseUrl == "/api/ems/leads" ? null : (
                    <Autocomplete
                      fullWidth
                      sx={
                        showInlinePersonTypeAndTrainer
                          ? {
                              width: { xs: "100%", md: "calc(50% - 8px)" },
                              display: "inline-flex",
                              verticalAlign: "top",
                            }
                          : undefined
                      }
                disableClearable={item.disableClearable === true}
                      options={effectiveOptions}
                      getOptionLabel={(option) =>
                        option ? String(item.formatValue(option) ?? "") : ""
                      }
                value={
                  (() => {
                    const currentVal = formData?.[item.mainKey];
                    if (
                      currentVal === null ||
                      currentVal === undefined ||
                      String(currentVal).trim() === ""
                    ) {
                      return null;
                    }
                    const matched = effectiveOptions.find((opt) => {
                      const optVal = getOptionSelectValue(
                        opt,
                        item.menuKey,
                        item.OptionMainKey
                      );
                      return isSameValue(optVal, currentVal);
                    });
                    if (matched) return matched;
                    if (item.OptionMainKey === "ReviewRatings") {
                      return REVIEW_RATING_BY_VALUE[Number(currentVal)] || null;
                    }
                    return null;
                  })()
                }
                      isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return false;
                        if (item.menuKey == null) {
                          return option === value;
                        }
                        return isSameValue(
                          option?.[item.menuKey],
                          value?.[item.menuKey]
                        );
                      }}
                      filterOptions={(options, state) => {
                        // Make search work reliably for different MUI versions/shapes.
                        let inputValue = "";
                        if (typeof state === "string") inputValue = state;
                        else if (state && typeof state === "object") {
                          inputValue =
                            state.inputValue ?? state.input ?? state.value ?? "";
                        }

                        const query = String(inputValue).trim().toLowerCase();
                        if (!query) return options;

                        return options.filter((opt) => {
                          const label = item.formatValue(opt);
                          return String(label ?? "")
                            .toLowerCase()
                            .includes(query);
                        });
                      }}
                      onChange={(event, newValue) => {
                        const value =
                          newValue == null
                            ? ""
                            : getOptionSelectValue(
                                newValue,
                                item.menuKey,
                                item.OptionMainKey
                              );

                        if (
                          baseUrl === "/api/ems/fee-installments" &&
                          item.mainKey === "status" &&
                          !guardFeeInstallmentStatusChange(value)
                        ) {
                          return;
                        }

                        handleChange({
                          target: { name: item.mainKey, value },
                        });

                        if (
                          baseUrl === "/api/ems/lead-activities" &&
                          item.mainKey === "organization_ems_lead_id"
                        ) {
                          void applyLeadActivityLeadConstraint(
                            value,
                            newValue,
                            formData?.activity_datetime
                          );
                        }
                      }}
                      renderOption={
                        item.useOptionEmoji &&
                        item.OptionMainKey === "ReviewRatings"
                          ? (props, option) => (
                              <Box
                                component="li"
                                {...props}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.25,
                                  py: 0.75,
                                }}
                              >
                                <ReviewRatingIcon
                                  value={option?.value}
                                  fontSize="medium"
                                  sx={{ fontSize: 28 }}
                                />
                                <Typography component="span" sx={{ fontSize: "0.95rem" }}>
                                  {option?.label ?? item.formatValue(option)}
                                </Typography>
                              </Box>
                            )
                          : undefined
                      }
                      disabled={
                        isViewMode ||
                        isItemDisabled ||
                        isAutoLockedStudentFeeInstallment ||
                        isLockedByContextPrefill ||
                        (mode === "edit" && item?.disableInEdit) ||
                        (isEmployeeOrTrainerField &&
                          !personTypeId &&
                          !(
                            (mode === "edit" || mode === "view") &&
                            formData?.[item.mainKey]
                          )) ||
                  ((isStudentFeeForm ||
                    isAssessmentResultForm ||
                    isStudentReviewForm) &&
                    isAdmissionField &&
                    !formData?.organization_ems_student_id) ||
                        (isStudentFeeForm &&
                          item.OptionMainKey === "FeeInstallments" &&
                          !formData?.organization_ems_student_id) ||
                        (baseUrl === "/api/ems/class-records" &&
                          item.OptionMainKey === "BatchClasses" &&
                          !formData?.organization_ems_batch_id) ||
                        (effectiveOptions?.length == 0 &&
                          !(
                            baseUrl === "/api/ems/batch-trainers" &&
                            item.mainKey === "trainer_employee_id" &&
                            (mode === "edit" || mode === "view") &&
                            formData?.trainer_employee_id &&
                            formData?.trainer
                          ))
                      }
                      renderInput={(params) => {
                        const isReviewRatingField =
                          item.useOptionEmoji &&
                          item.OptionMainKey === "ReviewRatings";
                        const ratingValue = formData?.[item.mainKey];
                        const showRatingIcon =
                          isReviewRatingField &&
                          ratingValue !== "" &&
                          ratingValue != null;
                        const ratingLabel = showRatingIcon
                          ? formatReviewRatingLabel(ratingValue)
                          : "";

                        return (
                          <TextField
                            {...params}
                            label={fieldLabel}
                            error={!!formErrors?.[item.mainKey]}
                            helperText={
                              baseUrl === "/api/ems/lead-activities" &&
                              item.mainKey === "organization_ems_lead_id" &&
                              formData?._lead_datetime
                                ? buildActivityDatetimeHelperText(
                                    formErrors?.[item.mainKey],
                                    formData._lead_datetime
                                  )
                                : formErrors?.[item.mainKey]
                            }
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: showRatingIcon ? (
                                <>
                                  <InputAdornment position="start">
                                    <ReviewRatingIcon
                                      value={ratingValue}
                                      fontSize="small"
                                      sx={{ fontSize: 24 }}
                                    />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ) : (
                                params.InputProps.startAdornment
                              ),
                            }}
                            inputProps={{
                              ...params.inputProps,
                              ...(showRatingIcon && ratingLabel
                                ? { value: ratingLabel, readOnly: true }
                                : {}),
                              ...(item.selectReadOnly ? { readOnly: true } : {}),
                            }}
                          />
                        );
                      }}
                    />
                  )}
                </>
              )}

              {item.type === "MultiSelect" && (
                <Autocomplete
                  multiple
                  fullWidth
                  disableCloseOnSelect
                  options={effectiveOptions}
                  getOptionLabel={(option) =>
                    option ? String(item.formatValue(option) ?? "") : ""
                  }
                  value={effectiveOptions.filter((opt) => {
                    const optVal = item.menuKey == null ? opt : opt?.[item.menuKey];
                    const currentVals = Array.isArray(formData?.[item.mainKey])
                      ? formData[item.mainKey]
                      : [];
                    return currentVals.some((v) => isSameValue(v, optVal));
                  })}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    if (item.menuKey == null) return option === value;
                    return isSameValue(
                      option?.[item.menuKey],
                      value?.[item.menuKey]
                    );
                  }}
                  onChange={(event, newValues) => {
                    const valueArray = (newValues || []).map((v) =>
                      item.menuKey == null ? v : v?.[item.menuKey]
                    );
                    handleChange({
                      target: { name: item.mainKey, value: valueArray },
                    });
                  }}
                  disabled={isViewMode || isItemDisabled || isLockedByContextPrefill}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={fieldLabel}
                      error={!!formErrors?.[item.mainKey]}
                      helperText={formErrors?.[item.mainKey]}
                    />
                  )}
                />
              )}

              {item.type === "CheckboxGroup" && (
                <FormControl
                  fullWidth
                  error={!!formErrors?.[item.mainKey]}
                  sx={{ mb: 1 }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {fieldLabel}
                  </Typography>
                  <FormGroup row>
                    {effectiveOptions.map((opt, optIdx) => {
                      const optVal = item.menuKey == null ? opt : opt?.[item.menuKey];
                      const currentVals = Array.isArray(formData?.[item.mainKey])
                        ? formData[item.mainKey]
                        : [];
                      const checked = currentVals.some((v) => isSameValue(v, optVal));
                      return (
                        <FormControlLabel
                          key={`${item.mainKey}-${String(optVal)}-${optIdx}`}
                          control={
                            <Checkbox
                              checked={checked}
                              onChange={(e) => {
                                const nextVals = e.target.checked
                                  ? [...currentVals, optVal]
                                  : currentVals.filter((v) => !isSameValue(v, optVal));
                                handleChange({
                                  target: { name: item.mainKey, value: nextVals },
                                });
                              }}
                              disabled={isViewMode || isItemDisabled || isLockedByContextPrefill}
                            />
                          }
                          label={String(item.formatValue(opt) ?? optVal ?? "")}
                        />
                      );
                    })}
                  </FormGroup>
                  {formErrors?.[item.mainKey] && (
                    <FormHelperText>{formErrors[item.mainKey]}</FormHelperText>
                  )}
                </FormControl>
              )}

              {item.type === "RadioGroup" && (
                <FormControl
                  fullWidth
                  error={!!formErrors?.[item.mainKey]}
                  component="fieldset"
                  sx={{ mb: 2 }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    {fieldLabel}
                  </Typography>

                  <RadioGroup
                    row
                    name={item.mainKey}
                    value={
                      (() => {
                        const rawVal = formData?.[item.mainKey];
                        if (
                          rawVal === null ||
                          rawVal === undefined ||
                          rawVal === ""
                        )
                          return "";
                        if (
                          rawVal === true ||
                          rawVal === "true" ||
                          rawVal === 1 ||
                          rawVal === "1"
                        )
                          return "true";
                        if (
                          rawVal === false ||
                          rawVal === "false" ||
                          rawVal === 0 ||
                          rawVal === "0"
                        )
                          return "false";
                        return String(rawVal);
                      })()
                    }
                    onChange={(e) => {
                      const selectedString = e.target.value;

                      const opt = effectiveOptions.find((o) => {
                        const optVal =
                          item.menuKey == null ? o : o?.[item.menuKey];
                        return String(optVal) === String(selectedString);
                      });

                      const derived =
                        selectedString === "true"
                          ? true
                          : selectedString === "false"
                            ? false
                            : selectedString;

                      const nextVal =
                        opt && item.menuKey != null ? opt?.[item.menuKey] : derived;

                      handleChange({
                        target: { name: item.mainKey, value: nextVal },
                      });
                    }}
                  >
                    {effectiveOptions.map((opt, optIdx) => {
                      const optVal =
                        item.menuKey == null ? opt : opt?.[item.menuKey];
                      const label =
                        typeof item.formatValue === "function"
                          ? item.formatValue(opt)
                          : opt?.label ?? optVal ?? "";

                      return (
                        <FormControlLabel
                          key={`${item.mainKey}-${String(optVal)}-${optIdx}`}
                          value={String(optVal)}
                          control={
                            <Radio
                              disabled={
                                isViewMode ||
                                isItemDisabled ||
                                isLockedByContextPrefill
                              }
                            />
                          }
                          label={String(label ?? "")}
                        />
                      );
                    })}
                  </RadioGroup>

                  {formErrors?.[item.mainKey] && (
                    <FormHelperText>{formErrors[item.mainKey]}</FormHelperText>
                  )}
                </FormControl>
              )}

              {item.type == "Text" && (
                <TextField
                  fullWidth
            label={fieldLabel}
                  name={item.mainKey}
                  value={formData?.[item?.mainKey]}
                  onChange={(e) => {
                    if (item.digitsOnly) {
                      const maxLen = item.maxLength ?? 20;
                      const digits = String(e.target.value)
                        .replace(/\D/g, "")
                        .slice(0, maxLen);
                      handleChange({
                        target: { name: item.mainKey, value: digits },
                      });
                      return;
                    }
                    handleChange(e);
                  }}
                  error={!!formErrors?.[item?.mainKey]}
                  helperText={formErrors?.[item?.mainKey]}
                  inputProps={
                    item.digitsOnly
                      ? {
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          maxLength: item.maxLength ?? 20,
                        }
                      : undefined
                  }
                  disabled={
                    isViewMode ||
                    isItemDisabled ||
                    isLockedByContextPrefill
                  }
                />
              )}

              {item.type == "Date" && (
                <TextField
                  fullWidth
                  type="date"
            label={fieldLabel}
                  name={item.mainKey}
                  value={formData?.[item?.mainKey]}
                  onChange={handleChange}
                  error={!!formErrors?.[item?.mainKey]}
                  helperText={
                    formErrors?.[item?.mainKey] ||
                    (typeof item.getHelperText === "function"
                      ? item.getHelperText(formData, mode)
                      : "") ||
                    (item.demoSessionDdMmYyyyHelper &&
                    baseUrl === "/api/ems/demo-sessions" &&
                    formData?.[item?.mainKey]
                      ? `Display format: ${formatDdMmYyyy(formData[item.mainKey])}`
                      : "")
                  }
                  inputProps={{
                    max:
                      baseUrl === "/api/ems/classes-attendance" &&
                      item.mainKey === "attendance_date"
                        ? (() => {
                            const d = new Date();
                            const pad = (n) =>
                              String(n).padStart(2, "0");
                            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
                          })()
                        : undefined,
                    min:
                      baseUrl === "/api/ems/fee-installments" &&
                      item.mainKey === "due_date"
                        ? getMinDueDateForStandaloneInstallment({
                            installmentNumber: formData?.installment_number,
                            existingInstallments:
                              formData?.existing_fee_installments,
                            excludeInstallmentId:
                              formData?.organization_ems_fee_installment_id ??
                              feeInstallmentEditId,
                          })
                        : undefined,
                  }}
                  InputLabelProps={{ shrink: true }}
                  disabled={
                    isViewMode ||
                    isItemDisabled ||
                    isLockedByContextPrefill ||
                    (baseUrl === "/api/ems/batch-students" &&
                      item.mainKey === "completion_date" &&
                      !["completed", "dropped"].includes(
                        String(formData?.batch_status ?? "").trim().toLowerCase()
                      ))
                  }
                />
              )}

              {item.type == "Time" && (
                <TextField
                  fullWidth
                  type="time"
            label={fieldLabel}
                  name={item.mainKey}
                  value={formData?.[item?.mainKey]}
                  onChange={handleChange}
                  error={!!formErrors?.[item?.mainKey]}
                  helperText={formErrors?.[item?.mainKey]}
                  InputLabelProps={{ shrink: true }}
                  disabled={
                    isViewMode ||
                    isItemDisabled ||
                    isLockedByContextPrefill ||
                    item?.disabled === true
                  }
                />
              )}

              {item.type == "Number" && (
                <TextField
                  fullWidth
                  type="number"
            label={fieldLabel}
                  name={item.mainKey}
                  value={formData?.[item?.mainKey]}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (
                      baseUrl === "/api/ems/training-programs" &&
                      raw !== "" &&
                      Number(raw) < 0
                    ) {
                      return;
                    }
                    handleChange(e);
                  }}
                  error={!!formErrors?.[item?.mainKey]}
                  helperText={formErrors?.[item?.mainKey]}
                  inputProps={
                    baseUrl === "/api/ems/training-programs"
                      ? {
                          min:
                            item.mainKey === "duration_in_hours"
                              ? 1
                              : item.mainKey === "duration_in_months"
                                ? 0.01
                                : 0,
                          step:
                            item.mainKey === "duration_in_hours" ? 1 : 0.01,
                        }
                      : undefined
                  }
                  disabled={
                    isViewMode ||
                    isItemDisabled ||
                    isLockedByContextPrefill
                  }
                />
              )}

              {item.type == "MultiRow" && (
                <>
                  {mode == "add" && item.mainKey == "spam_reason" && baseUrl == "/api/ems/leads" ? null : (
                    <TextField
                      fullWidth
                label={fieldLabel}
                      name={item.mainKey}
                      value={formData?.[item?.mainKey]}
                      onChange={handleChange}
                      error={!!formErrors?.[item?.mainKey]}
                      helperText={formErrors?.[item?.mainKey]}
                      multiline
                      rows={3}
                      disabled={
                        isViewMode ||
                        isItemDisabled ||
                        isLockedByContextPrefill ||
                        (item.mainKey === "spam_reason" &&
                          baseUrl === "/api/ems/leads" &&
                          !formData?.is_spam)
                      }
                    />
                  )}
                </>
              )}

              {item.type === "CurrencyAmountTable" && (
                <Box sx={{ width: "100%" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {fieldLabel}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Currency</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(Array.isArray(formData?.[item.mainKey]) ? formData[item.mainKey] : [])
                          .map((row, rowIndex) => (
                            <TableRow key={`${row.currency_code}-${rowIndex}`}>
                              <TableCell>
                                {row.currency_code}
                                {row.currency_name ? ` - ${row.currency_name}` : ""}
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="number"
                                  size="small"
                                  value={row.amount ?? ""}
                                  onChange={(e) => {
                                    let nextValue = e.target.value;
                                    if (
                                      baseUrl === "/api/ems/training-programs" &&
                                      nextValue !== "" &&
                                      Number(nextValue) < 0
                                    ) {
                                      return;
                                    }
                                    setFormData((prev) => {
                                      const currentRows = Array.isArray(prev?.[item.mainKey])
                                        ? prev[item.mainKey]
                                        : [];
                                      const nextRows = currentRows.map((r, i) =>
                                        i === rowIndex ? { ...r, amount: nextValue } : r
                                      );
                                      return { ...prev, [item.mainKey]: nextRows };
                                    });
                                    setFormErrors((prev) => ({ ...prev, [item.mainKey]: "" }));
                                  }}
                                  disabled={isViewMode}
                                  placeholder="Enter amount"
                                  inputProps={
                                    baseUrl === "/api/ems/training-programs"
                                      ? { min: 0.01, step: 0.01 }
                                      : undefined
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        {(!Array.isArray(formData?.[item.mainKey]) ||
                          formData[item.mainKey].length === 0) && (
                          <TableRow>
                            <TableCell colSpan={2} align="center">
                              No currencies available.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {formErrors?.[item.mainKey] && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {formErrors[item.mainKey]}
                    </FormHelperText>
                  )}
                </Box>
              )}

              {item.type === "TimeSlotsTable" && (
                <Box
                  sx={{
                    width: "100%",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 1.5,
                    bgcolor: "background.paper",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.25,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {fieldLabel}
                    </Typography>
                    {!isViewMode && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setFormData((prev) => {
                            const currentRows = Array.isArray(prev?.[item.mainKey])
                              ? prev[item.mainKey]
                              : [];
                            return {
                              ...prev,
                              [item.mainKey]: [...currentRows, { start_time: "", end_time: "" }],
                            };
                          });
                          setFormErrors((prev) => ({ ...prev, [item.mainKey]: "" }));
                        }}
                      >
                        Add Slot
                      </Button>
                    )}
                  </Box>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ borderRadius: 2, overflow: "auto", maxWidth: "100%" }}
                  >
                    <Table size="small">
                      <TableHead sx={{ bgcolor: "action.hover" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, width: 64 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
                          {!isViewMode && (
                            <TableCell sx={{ fontWeight: 600, width: 80 }}>Action</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(Array.isArray(formData?.[item.mainKey]) ? formData[item.mainKey] : []).map(
                          (row, rowIndex) => (
                            <TableRow key={`ts-${rowIndex}`} hover>
                              <TableCell sx={{ color: "text.secondary", fontWeight: 600 }}>
                                {rowIndex + 1}
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="time"
                                  size="small"
                                  value={toTimeInputValue(row?.start_time)}
                                  onChange={(e) => {
                                    const nextValue = e.target.value;
                                    setFormData((prev) => {
                                      const currentRows = Array.isArray(prev?.[item.mainKey])
                                        ? prev[item.mainKey]
                                        : [];
                                      const nextRows = currentRows.map((r, i) =>
                                        i === rowIndex ? { ...r, start_time: nextValue } : r
                                      );
                                      return { ...prev, [item.mainKey]: nextRows };
                                    });
                                    setFormErrors((prev) => ({ ...prev, [item.mainKey]: "" }));
                                  }}
                                  disabled={isViewMode}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="time"
                                  size="small"
                                  value={toTimeInputValue(row?.end_time)}
                                  onChange={(e) => {
                                    const nextValue = e.target.value;
                                    setFormData((prev) => {
                                      const currentRows = Array.isArray(prev?.[item.mainKey])
                                        ? prev[item.mainKey]
                                        : [];
                                      const nextRows = currentRows.map((r, i) =>
                                        i === rowIndex ? { ...r, end_time: nextValue } : r
                                      );
                                      return { ...prev, [item.mainKey]: nextRows };
                                    });
                                    setFormErrors((prev) => ({ ...prev, [item.mainKey]: "" }));
                                  }}
                                  disabled={isViewMode}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                                />
                              </TableCell>
                              {!isViewMode && (
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setFormData((prev) => {
                                        const currentRows = Array.isArray(prev?.[item.mainKey])
                                          ? prev[item.mainKey]
                                          : [];
                                        const nextRows = currentRows.filter((_, i) => i !== rowIndex);
                                        return {
                                          ...prev,
                                          [item.mainKey]:
                                            nextRows.length > 0 ? nextRows : [{ start_time: "", end_time: "" }],
                                        };
                                      });
                                      setFormErrors((prev) => ({ ...prev, [item.mainKey]: "" }));
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          )
                        )}
                        {(!Array.isArray(formData?.[item.mainKey]) ||
                          formData[item.mainKey].length === 0) && (
                          <TableRow>
                            <TableCell colSpan={isViewMode ? 3 : 4} align="center">
                              <Typography variant="body2" color="text.secondary">
                                No time slots added yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {formErrors?.[item.mainKey] && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {formErrors[item.mainKey]}
                    </FormHelperText>
                  )}
                </Box>
              )}

        {item.type === "InstallmentsTable" &&
          (() => {
            const icRaw = formData?.installment_count;
            const icNormalized =
              icRaw === null || icRaw === undefined
                ? ""
                : String(icRaw).trim();
            const icNum = icNormalized === "" ? NaN : Number(icNormalized);
            const showFeeInstallmentsTable =
              icNormalized !== "" &&
              Number.isFinite(icNum) &&
              icNum >= 1;

            return (
                <Box sx={{ width: "100%" }}>
                {showFeeInstallmentsTable ? (
                  <>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Installment #</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Due Date *</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Fee Instalment INR *</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Fee Instalment
                          {formData?.currency_code ? ` ${formData.currency_code}` : ""}
                          </TableCell>
                            {!isViewMode && (
                              <TableCell sx={{ fontWeight: 600, width: 80 }}>
                                Action
                              </TableCell>
                            )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(Array.isArray(formData?.[item.mainKey]) ? formData[item.mainKey] : []).map(
                          (row, rowIndex) => (
                            <TableRow key={`fi-${rowIndex}`}>
                              <TableCell>{row.installment_number}</TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="date"
                                  size="small"
                                  value={row.due_date ?? ""}
                                  onChange={(e) => {
                                    const nextValue = e.target.value;
                                    const currentRows = Array.isArray(formData?.[item.mainKey])
                                      ? formData[item.mainKey]
                                      : [];
                                    const nextRows = currentRows.map((r, i) =>
                                      i === rowIndex ? { ...r, due_date: nextValue } : r
                                    );
                                    const orderError =
                                      getInstallmentDueDateOrderError(nextRows);
                                    setFormData((prev) => ({
                                      ...prev,
                                      [item.mainKey]: nextRows,
                                    }));
                                    setFormErrors((prev) => ({
                                      ...prev,
                                      [item.mainKey]: orderError || "",
                                    }));
                                  }}
                                  disabled={isViewMode}
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{
                                    min: getMinDueDateForInstallmentRow(
                                      formData?.[item.mainKey],
                                      rowIndex
                                    ),
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="number"
                                  size="small"
                                  value={row.fee_instalment_inr ?? ""}
                                  onChange={(e) => {
                                    const nextValue = e.target.value;
                                    setFormData((prev) => {
                                      const currentRows = Array.isArray(prev?.[item.mainKey])
                                        ? prev[item.mainKey]
                                        : [];
                                      const nextRows = currentRows.map((r, i) =>
                                        i === rowIndex
                                          ? { ...r, fee_instalment_inr: nextValue }
                                          : r
                                      );
                                      const amountError = validateFeeInstallmentAmounts({
                                        installments: nextRows,
                                        netInr: prev?.net_fee_amount,
                                        netStudent: prev?.net_fee_amount_student_currency,
                                      });
                                      setFormErrors((formErrPrev) => ({
                                        ...formErrPrev,
                                        [item.mainKey]: amountError || "",
                                      }));
                                      return { ...prev, [item.mainKey]: nextRows };
                                    });
                                  }}
                                  disabled={isViewMode}
                                  inputProps={{ min: 0, step: "0.01" }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="number"
                                  size="small"
                                value={row.fee_instalment_student_student_currency ?? ""}
                                  onChange={(e) => {
                                    const nextValue = e.target.value;
                                    setFormData((prev) => {
                                      const currentRows = Array.isArray(prev?.[item.mainKey])
                                        ? prev[item.mainKey]
                                        : [];
                                      const nextRows = currentRows.map((r, i) =>
                                        i === rowIndex
                                          ? {
                                              ...r,
                                              fee_instalment_student_student_currency: nextValue,
                                            }
                                          : r
                                      );
                                      const amountError = validateFeeInstallmentAmounts({
                                        installments: nextRows,
                                        netInr: prev?.net_fee_amount,
                                        netStudent: prev?.net_fee_amount_student_currency,
                                      });
                                      setFormErrors((formErrPrev) => ({
                                        ...formErrPrev,
                                        [item.mainKey]: amountError || "",
                                      }));
                                      return { ...prev, [item.mainKey]: nextRows };
                                    });
                                  }}
                                  disabled={isViewMode}
                                  inputProps={{ min: 0, step: "0.01" }}
                                />
                              </TableCell>
                                {!isViewMode && (
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={
                                        (Array.isArray(formData?.[item.mainKey])
                                          ? formData[item.mainKey].length
                                          : 0) <= 1
                                      }
                                      onClick={() => {
                                        const currentRows = Array.isArray(formData?.[item.mainKey])
                                          ? formData[item.mainKey]
                                          : [];
                                        if (currentRows.length <= 1) return;
                                        const nextRows = currentRows.filter(
                                          (_, i) => i !== rowIndex
                                        );
                                        setFormData((prev) => ({
                                          ...prev,
                                          [item.mainKey]: nextRows,
                                          installment_count: Math.max(
                                            Number(prev?.installment_count || nextRows.length) - 1,
                                            1
                                          ),
                                        }));
                                        setFormErrors((prev) => ({
                                          ...prev,
                                          [item.mainKey]: "",
                                        }));
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                )}
                            </TableRow>
                          )
                        )}
                        {(!Array.isArray(formData?.[item.mainKey]) ||
                          formData[item.mainKey].length === 0) && (
                          <TableRow>
                              <TableCell colSpan={!isViewMode ? 5 : 4} align="center">
                              Enter installment count to generate rows.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                    {!isViewMode && (
                      <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            const existingRows = Array.isArray(formData?.[item.mainKey])
                              ? formData[item.mainKey]
                              : [];
                            const nextCount = Math.max(
                              Number(formData?.installment_count || 0),
                              existingRows.length
                            ) + 1;
                            setFormData((prev) => ({
                              ...prev,
                              installment_count: nextCount,
                            }));
                          }}
                        >
                          Add Installment
                        </Button>
                      </Box>
                    )}
                  </>
                ) : null}
                {showFeeInstallmentsTable && formErrors?.[item.mainKey] && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {formErrors[item.mainKey]}
                    </FormHelperText>
                  )}
                {showFeeInstallmentsTable && isAdmissionsForm && (
                  <FormHelperText sx={{ mt: 1 }}>
                    {(() => {
                      const rows = Array.isArray(formData?.[item.mainKey])
                        ? formData[item.mainKey]
                        : [];
                      const totals = sumInstallmentAmounts(rows);
                      const expectedInr = Number(formData?.net_fee_amount || 0);
                      const expectedStudent = Number(
                        formData?.net_fee_amount_student_currency || 0
                      );
                      const studentLabel = formData?.currency_code
                        ? ` ${formData.currency_code}`
                        : " (student currency)";
                      return `Installment totals: INR ${totals.inr} / ${expectedInr}${studentLabel}: ${totals.student} / ${expectedStudent}`;
                    })()}
                  </FormHelperText>
                )}
                </Box>
            );
          })()}

        {item.type === "Picture" &&
          (() => {
                const file = formData?.[item.mainKey];
            const pictureAlign =
              item.align === "left" ? "flex-start" : "center";
                return (
                  <Box
                    key={idx}
                    display="flex"
                    flexDirection="column"
                alignItems={pictureAlign}
                    py={1}
                  >
                    <Box sx={{ position: "relative", display: "inline-block" }}>
                      <Avatar
                        src={
                          file instanceof File
                            ? URL.createObjectURL(file)
                            : file
                              ? `${file}`
                              : ""
                        }
                        alt="Profile"
                        sx={{ width: 96, height: 96 }}
                      />
                      {file && !isViewMode && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            handleRemoveFile(item.mainKey);
                            if (mode === "edit") {
                              setFormData((prev) => ({ ...prev, [`_remove_${item.mainKey}`]: "1" }));
                            }
                          }}
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            backgroundColor: "#fff",
                            border: "1px solid #ddd",
                            width: 22,
                            height: 22,
                            padding: 0,
                            "&:hover": { backgroundColor: "#ffebee", borderColor: "#e57373" },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 14, color: "#e53935" }} />
                        </IconButton>
                      )}
                    </Box>

                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      sx={{ mt: 1, textTransform: "none" }}
                      disabled={isViewMode}
                    >
                      {mode === "edit" || mode === "view" ? "Edit Image" : "Upload Image"}
                      {!isViewMode && (
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                      onChange={(e) => handleChangeImage(item.mainKey, e.target.files[0])}
                        />
                      )}
                    </Button>
                    {formErrors?.[item.mainKey] && (
                  <FormHelperText error>{formErrors?.[item.mainKey]}</FormHelperText>
                    )}
                  </Box>
                );
              })()}

        {item.type === "File" &&
          (() => {
                const file = formData?.[item.mainKey];
                return (
                  <Box key={idx} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        component="label"
                        size="small"
                        sx={{
                          textTransform: "none",
                          fontWeight: 500,
                          py: 0.5,
                          px: 1.5,
                          borderRadius: 1.5,
                          flexGrow: 0,
                          width: "auto",
                          maxWidth: "100%",
                        }}
                        disabled={isViewMode}
                      >
                    {file ? `Change ${fieldLabel}` : `Choose ${fieldLabel}`}
                        {!isViewMode && (
                          <input
                            type="file"
                            hidden
                            onChange={(e) => handleFileChange(e, item.mainKey)}
                          />
                        )}
                      </Button>

                      {file && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handlePreview(file)}
                            sx={{
                              backgroundColor: "#e3f2fd",
                              color: "#1976d2",
                              "&:hover": { backgroundColor: "#bbdefb" },
                              border: "1px solid #ddd",
                              width: 32,
                              height: 32,
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>

                          {!isViewMode && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveFile(item.mainKey)}
                              sx={{
                                backgroundColor: "#f5f5f5",
                                "&:hover": {
                                  backgroundColor: "#e57373",
                                  color: "#fff",
                                },
                                border: "1px solid #ddd",
                                width: 32,
                                height: 32,
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </>
                      )}
                    </Box>

                    {file && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#2e7d32",
                          fontWeight: 500,
                          mt: 0.5,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        Selected: {file.name}
                      </Typography>
                    )}
                    {formErrors?.[item.mainKey] && (
                  <FormHelperText error>{formErrors?.[item.mainKey]}</FormHelperText>
                    )}
                  </Box>
                );
              })()}

              {item.type === "DateTime" && (
                <TextField
                  fullWidth
                  type="datetime-local"
            label={fieldLabel}
                  name={item.mainKey}
                  value={formData?.[item?.mainKey] || ""}
                  onChange={(e) => {
                    handleChange(e);
                    if (
                      baseUrl === "/api/ems/lead-activities" &&
                      item.mainKey === "activity_datetime"
                    ) {
                      const msg = validateActivityDatetimeAgainstLead(
                        e.target.value,
                        formData?._lead_datetime
                      );
                      setFormErrors((prev) => ({
                        ...prev,
                        activity_datetime: msg || "",
                      }));
                    }
                  }}
                  error={!!formErrors?.[item?.mainKey]}
                  helperText={
                    baseUrl === "/api/ems/lead-activities" &&
                    item.mainKey === "activity_datetime" &&
                    formData?._lead_datetime
                      ? buildActivityDatetimeHelperText(
                          formErrors?.[item?.mainKey],
                          formData._lead_datetime
                        )
                      : formErrors?.[item?.mainKey]
                  }
                  inputProps={{
                    max:
                      baseUrl === "/api/ems/leads" &&
                      item.mainKey === "lead_datetime"
                        ? toDatetimeLocalInputValueEmbed(new Date())
                        : undefined,
                    min:
                      baseUrl === "/api/ems/lead-activities" &&
                      item.mainKey === "activity_datetime" &&
                      formData?._lead_datetime
                        ? formData._lead_datetime
                        : undefined,
                  }}
                  InputLabelProps={{ shrink: true }}
                  disabled={isViewMode}
                />
              )}

              {item.type === "Checkbox" && (
                <>
                  {mode == "add" && item.mainKey == "is_spam" && baseUrl == "/api/ems/leads" ? null : (
                    <FormControl
                      key={idx}
                      error={!!formErrors?.[item.mainKey]}
                      component="fieldset"
                      sx={{ mb: 2 }}
                      fullWidth
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formData?.[item.mainKey]}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFormData((prev) => ({
                                ...prev,
                                [item.mainKey]: checked,
                                ...(item.mainKey === "is_spam" &&
                                baseUrl === "/api/ems/leads" &&
                                !checked
                                  ? { spam_reason: "" }
                                  : {}),
                              }));
                            }}
                            name={item.mainKey}
                            disabled={
                              isViewMode ||
                              isItemDisabled ||
                              isLockedByContextPrefill
                            }
                          />
                        }
                  label={fieldLabel}
                      />
                      {formErrors?.[item.mainKey] && (
                        <FormHelperText>{formErrors[item.mainKey]}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              )}
            </React.Fragment>
          );
  };

  return (
    <CommonFormLayout
      mode={mode}
      loading={loading}
      btnLoading={isDemoParticipantsAddMode ? participantBtnLoading : btnLoading}
      onSubmit={
        isDemoParticipantsAddMode
          ? handleParticipantAccordionsSubmit
          : handleMainFormSubmit
      }
      headerProps={headerProps}
      layout={fieldGridLayout?.rows?.length ? { contentColumns: 12 } : undefined}
    >
      {isDemoParticipantsAddMode ? (
        <Grid item xs={12}>
          {participantForms.map((entry, formIndex) => (
            <Accordion
              key={entry.key}
              expanded={expandedParticipantKey === entry.key}
              onChange={(event, isExpanded) =>
                setExpandedParticipantKey(isExpanded ? entry.key : null)
              }
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pr: 1,
                  }}
                >
                  <Typography fontWeight={600}>
                    Participant {formIndex + 1}
                  </Typography>
                  {participantForms.length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeParticipantAccordion(formIndex);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {Inputs?.map((item, idx) => {
                    const rawOptions = selectOptions?.[item.OptionMainKey] || [];
                    const fieldLabel =
                      typeof item.getLabel === "function"
                        ? item.getLabel(entry.formData)
                        : item.label;
                    return (
                      <React.Fragment key={`${entry.key}-${idx}`}>
                        {item.type == "Select" && (
                          <Autocomplete
                            fullWidth
                            options={rawOptions}
                            getOptionLabel={(option) =>
                              option ? String(item.formatValue(option) ?? "") : ""
                            }
                            value={
                              rawOptions.find((opt) => {
                                const optVal = item.menuKey == null ? opt : opt?.[item.menuKey];
                                return isSameValue(optVal, entry.formData?.[item.mainKey]);
                              }) || null
                            }
                            isOptionEqualToValue={(option, value) => {
                              if (!option || !value) return false;
                              if (item.menuKey == null) return option === value;
                              return isSameValue(
                                option?.[item.menuKey],
                                value?.[item.menuKey]
                              );
                            }}
                            filterOptions={(options, state) => {
                              let inputValue = "";
                              if (typeof state === "string") inputValue = state;
                              else if (state && typeof state === "object") {
                                inputValue =
                                  state.inputValue ?? state.input ?? state.value ?? "";
                              }
                              const query = String(inputValue).trim().toLowerCase();
                              if (!query) return options;
                              return options.filter((opt) =>
                                String(item.formatValue(opt) ?? "")
                                  .toLowerCase()
                                  .includes(query)
                              );
                            }}
                            onChange={(event, newValue) => {
                              const value =
                                newValue == null
                                  ? ""
                                  : item.menuKey == null
                                    ? newValue
                                    : newValue?.[item.menuKey];
                              handleAccordionFieldChange(formIndex, item.mainKey, value);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={fieldLabel}
                                error={!!entry.formErrors?.[item.mainKey]}
                                helperText={entry.formErrors?.[item.mainKey]}
                              />
                            )}
                          />
                        )}
                        {item.type == "Text" && (
                          <TextField
                            fullWidth
                            label={fieldLabel}
                            name={item.mainKey}
                            value={entry.formData?.[item?.mainKey]}
                            onChange={(e) =>
                              handleAccordionFieldChange(
                                formIndex,
                                item.mainKey,
                                e.target.value
                              )
                            }
                            error={!!entry.formErrors?.[item?.mainKey]}
                            helperText={entry.formErrors?.[item?.mainKey]}
                          />
                        )}
                        {item.type == "MultiRow" && (
                          <TextField
                            fullWidth
                            label={fieldLabel}
                            name={item.mainKey}
                            value={entry.formData?.[item?.mainKey]}
                            onChange={(e) =>
                              handleAccordionFieldChange(
                                formIndex,
                                item.mainKey,
                                e.target.value
                              )
                            }
                            error={!!entry.formErrors?.[item?.mainKey]}
                            helperText={entry.formErrors?.[item?.mainKey]}
                            multiline
                            rows={3}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addParticipantAccordion}
            sx={{ mt: 1 }}
          >
            Add Participant
          </Button>
        </Grid>
      ) : (
      <>
        {fieldGridLayout?.rows?.length
          ? fieldGridLayout.rows.map((row, rowIdx) => {
              const section = fieldGridLayout.sections?.find(
                (s) => s.beforeRow === rowIdx
              );
              const variant = row.variant || "threeEqual";
              const keyCount = row.mainKeys?.length || 0;
              const gridCols = rowGridTemplateColumns(row, variant, keyCount);
              const n = fieldGridLayout.spacingAfterEveryRows;
              const addGapAfterRowGroup =
                n &&
                (rowIdx + 1) % n === 0 &&
                rowIdx < fieldGridLayout.rows.length - 1;
              return (
                <Box
                  key={`fgl-row-${rowIdx}`}
                  sx={{
                    width: "100%",
                    flexBasis: "100%",
                    ...(addGapAfterRowGroup ? { mb: 2 } : {}),
                  }}
                >
                  {section && (
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        mt: rowIdx > 0 ? 3 : 0,
                        mb: 1,
                        ...(section.sx || {}),
                      }}
                    >
                      {section.title}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: "grid",
                      width: "100%",
                      columnGap: 2,
                      rowGap: 2,
                      gridTemplateColumns: gridCols,
                    }}
                  >
                    {row.mainKeys?.map((mk) => {
                      const idx = inputIndexByMainKey[mk];
                      const item = Inputs?.[idx];
                      if (!item) return null;
                      return (
                        <Box key={mk} sx={{ minWidth: 0, width: "100%" }}>
                          {renderInputField(item, idx)}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })
          : Inputs?.map((item, idx) => renderInputField(item, idx))}
      </>
      )}
    </CommonFormLayout>
  );
}

export default CommonForm;
